import {
    symbolLayoutAttributes,
    symbolGlobeExtAttributes,
    collisionVertexAttributes,
    collisionVertexAttributesExt,
    collisionBoxLayout,
    dynamicLayoutAttributes,
    iconTransitioningAttributes,
    zOffsetAttributes,
    orientationAttributes,
    featureIdAttributes
} from './symbol_attributes';
import {SymbolLayoutArray,
    SymbolGlobeExtArray,
    SymbolDynamicLayoutArray,
    SymbolOpacityArray,
    CollisionBoxLayoutArray,
    CollisionVertexExtArray,
    CollisionVertexArray,
    PlacedSymbolArray,
    SymbolInstanceArray,
    GlyphOffsetArray,
    SymbolLineVertexArray,
    SymbolIconTransitioningArray,
    ZOffsetVertexArray,
    StructArrayLayout1f4
} from '../array_types';
import {SymbolPropertyBinderUBO} from './symbol_property_binder_ubo';
import ONE_EM from '../../symbol/one_em';
import Point from '@mapbox/point-geometry';
import SegmentVector, {type Segment} from '../segment';
import {ProgramConfigurationSet} from '../program_configuration';
import {TriangleIndexArray, LineIndexArray} from '../index_array_type';
import transformText from '../../symbol/transform_text';
import mergeLines from '../../symbol/mergelines';
import {allowsVerticalWritingMode, stringContainsRTLText} from '../../util/script_detection';
import {WritingMode, fitIconToText, shapeIcon, type Shaping} from '../../symbol/shaping';
import loadGeometry from '../load_geometry';
import toEvaluationFeature, {type EvaluationFeature} from '../evaluation_feature';
import {VectorTileFeature} from '@mapbox/vector-tile';
const vectorTileFeatureTypes = VectorTileFeature.types;
import {verticalizedCharacterMap} from '../../util/verticalize_punctuation';
import {evaluateSizeForFeature, evaluateSizeForZoom, getSizeData, SIZE_PACK_FACTOR} from '../../symbol/symbol_size';
import {computeFontScale, getAppearanceIconValues, getAppearanceTextValues, getScaledImageVariant, packSizeForVertex, MAX_PACKED_SIZE} from '../../symbol/symbol_layout';
import {register} from '../../util/web_worker_transfer';
import EvaluationParameters from '../../style/evaluation_parameters';
import Formatted from '../../style-spec/expression/types/formatted';
import ResolvedImage from '../../style-spec/expression/types/resolved_image';
import {ImageVariant as ImageVariantClass} from '../../style-spec/expression/types/image_variant';
import {plugin as globalRTLTextPlugin, getRTLTextPluginStatus} from '../../source/rtl_text_plugin';
import {resamplePred} from '../../geo/projection/resample';
import {tileCoordToECEF} from '../../geo/projection/globe_util';
import {getProjection} from '../../geo/projection/index';
import {mat4, quat, vec3} from 'gl-matrix';
import assert from 'assert';
import {regionsEquals} from '../../../3d-style/source/replacement_source';
import {clamp} from '../../util/util';
import {tileToMeter} from '../../geo/mercator_coordinate';
import {type CollisionBoxArray, type CollisionBox, type SymbolInstance, SymbolOrientationArray} from '../array_types';
import {type SymbolQuad, getIconQuads, getGlyphQuads} from '../../symbol/quads';
import {FeatureAppearances, UNINITIALIZED_APPEARANCE_INDEX} from './feature_appearances';

import type {VectorTileLayer} from '@mapbox/vector-tile';
import type Anchor from '../../symbol/anchor';
import type {Region, ReplacementSource} from '../../../3d-style/source/replacement_source';
import type {PossiblyEvaluatedPropertyValue, PropertyValue} from '../../style/properties';
import type SymbolStyleLayer from '../../style/style_layer/symbol_style_layer';
import type {Class} from '../../types/class';
import type {ProjectionSpecification} from '../../style-spec/types';
import type Projection from '../../geo/projection/projection';
import type {CanonicalTileID, OverscaledTileID, UnwrappedTileID} from '../../source/tile_id';
import type {
    Bucket,
    BucketParameters,
    IndexedFeature,
    PopulateParameters
} from '../bucket';
import type {StructArray, StructArrayMember} from '../../util/struct_array';
import type Context from '../../gl/context';
import type IndexBuffer from '../../gl/index_buffer';
import type VertexBuffer from '../../gl/vertex_buffer';
import type {SizeData, InterpolatedSize} from '../../symbol/symbol_size';
import type {FeatureStates} from '../../source/source_state';
import type {TileTransform} from '../../geo/projection/tile_transform';
import type {TileFootprint} from '../../../3d-style/util/conflation';
import type {LUT} from '../../util/lut';
import type {SpritePositions} from '../../util/image';
import type {TypedStyleLayer} from '../../style/style_layer/typed_style_layer';
import type {ElevationType} from '../../../3d-style/elevation/elevation_constants';
import type {ImageId} from '../../style-spec/expression/types/image_id';
import type {ElevationFeature} from '../../../3d-style/elevation/elevation_feature';
import type {ImageVariant, StringifiedImageVariant} from '../../style-spec/expression/types/image_variant';
import type {ImagePositionMap} from '../../render/image_atlas';
import type {StyleImage} from '../../style/style_image';
import type SymbolAppearance from '../../style/appearance';
import type {AppearanceProps} from '../../style/appearance_properties';
import type {FeatureState, GlobalProperties} from '../../style-spec/expression';
import type ImageManager from '../../render/image_manager';
import type {AppearanceUpdateResult} from './feature_appearances';

export type AppearanceFeatureData = {
    // Identity — set during populate() on the worker thread, never mutated afterwards.
    id?: string | number | null;
    properties: Record<PropertyKey, unknown>;
    usesAppearanceIconAsPlaceholder: boolean;

    // Cached vertex snapshots — written on first appearance activation, restored on deactivation.
    layoutBasedIconVertexData: Uint16Array;
    layoutBasedTextVertexData: Uint16Array;

    // Mutable render state — updated every frame by updateAppearances() on the main thread.
    isUsingAppearanceIconVertexData: boolean;
    isUsingAppearanceTextVertexData: boolean;
    // Index into layer.appearances[]; -1 means no appearance is active (default rendering).
    // Initialized to UNINITIALIZED_APPEARANCE_INDEX — ensures the first updateAppearances() call
    // always runs the full update path (so placeholder icons get zeroed, etc.).
    activeAppearanceIndex: number;

    // Layout dependencies — written during worker-thread layout (addTextVertices), read on main thread.
    textShaping?: Shaping;
    iconTextFitPadding?: [number, number, number, number];
    fontScale?: number;
    textScaleFactor?: number;
};

// Per-frame context passed to updateSymbolInstanceIconVertices.
// Groups the parameters that are fixed for the entire updateAppearances() call
// or evaluated once per feature from layout, separating them from per-instance
// identifiers (symbolInstance, featureData, activeAppearance, evaluationFeature, vertexOffset).
type AppearanceIconUpdateContext = {
    // Shared fields (same order in both context types)
    canonical: CanonicalTileID;
    layer: SymbolStyleLayer;
    featureState: FeatureState;
    availableImages: Array<ImageId>;
    // Icon-specific fields
    globalProperties: GlobalProperties;
    iconScaleFactor: number;
    layoutIconOffset: [number, number];
    layoutIconSize: number;
    layoutIconRotate: number;
};

// Per-frame context passed to updateSymbolInstanceTextVertices.
type AppearanceTextUpdateContext = {
    // Shared fields (same order in both context types)
    canonical: CanonicalTileID;
    layer: SymbolStyleLayer;
    featureState: FeatureState;
    availableImages: Array<ImageId>;
    // Text-specific fields
    textScaleFactor: number;
    imageMap: Map<StringifiedImageVariant, StyleImage>;
    layoutTextOffset: [number, number];
    layoutTextSize: number;
    layoutTextRotate: number;
    layoutMinZoomSize: number;
    layoutMaxZoomSize: number;
    layoutTextSizeMinZoom: number;
    layoutTextSizeMaxZoom: number;
};

export type SingleCollisionBox = {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    padding: number;
    projectedAnchorX: number;
    projectedAnchorY: number;
    projectedAnchorZ: number;
    tileAnchorX: number;
    tileAnchorY: number;
    elevation?: number;
    tileID?: OverscaledTileID;
};

export type CollisionArrays = {
    textBox?: SingleCollisionBox;
    verticalTextBox?: SingleCollisionBox;
    iconBox?: SingleCollisionBox;
    verticalIconBox?: SingleCollisionBox;
    textFeatureIndex?: number;
    verticalTextFeatureIndex?: number;
    iconFeatureIndex?: number;
    verticalIconFeatureIndex?: number;
};

export type SymbolFeature = {
    sortKey: number | undefined;
    text: Formatted | undefined;
    icon: ResolvedImage | null | undefined;
    index: number;
    sourceLayerIndex: number;
    geometry: Array<Array<Point>>;
    properties: VectorTileFeature["properties"];
    type: 'Unknown' | 'Point' | 'LineString' | 'Polygon';
    id?: number | string | undefined;
};

export type SortKeyRange = {
    sortKey: number;
    symbolInstanceStart: number;
    symbolInstanceEnd: number;
};

type LineVertexRange = {
    lineLength: number;
    lineStartIndex: number;
};

type GlobeData = {
    anchor: Anchor;
    up: vec3;
};

// Opacity arrays are frequently updated but don't contain a lot of information, so we pack them
// tight. Each Uint32 is actually four duplicate Uint8s for the four corners of a glyph
// 7 bits are for the current opacity, and the lowest bit is the target opacity

// actually defined in symbol_attributes.js
// const placementOpacityAttributes = [
//     { name: 'a_fade_opacity', components: 1, type: 'Uint32' }
// ];
const shaderOpacityAttributes: ReadonlyArray<StructArrayMember> = [
    {name: 'a_fade_opacity', components: 1, type: 'Uint8', offset: 0}
];

function addVertex(array: SymbolLayoutArray, tileAnchorX: number, tileAnchorY: number, ox: number, oy: number, tx: number, ty: number, sizeVertex: number[], isSDF: boolean, pixelOffsetX: number, pixelOffsetY: number, minFontScaleX: number, minFontScaleY: number) {
    const aSizeX = sizeVertex ? Math.min(MAX_PACKED_SIZE, Math.round(sizeVertex[0])) : 0;
    const aSizeY = sizeVertex ? Math.min(MAX_PACKED_SIZE, Math.round(sizeVertex[1])) : 0;

    array.emplaceBack(
        // a_pos_offset
        tileAnchorX,
        tileAnchorY,
        Math.round(ox * 32),
        Math.round(oy * 32),

        // a_data
        tx, // x coordinate of symbol on atlas texture
        ty, // y coordinate of symbol on atlas texture
        (aSizeX << 1) + (isSDF ? 1 : 0),
        (aSizeY << 1) + 0,  // pack isAppearanceIcon flag in last bit, 0 for non-appearance icon
        pixelOffsetX * 16,
        pixelOffsetY * 16,
        minFontScaleX * 256,
        minFontScaleY * 256
    );
}

function addTransitioningVertex(array: SymbolIconTransitioningArray, tx: number, ty: number) {
    array.emplaceBack(tx, ty);
}

function addGlobeVertex(array: SymbolGlobeExtArray, projAnchorX: number, projAnchorY: number, projAnchorZ: number, normX: number, normY: number, normZ: number) {
    array.emplaceBack(
        // a_globe_anchor
        projAnchorX,
        projAnchorY,
        projAnchorZ,

        // a_globe_normal
        normX,
        normY,
        normZ
    );
}

function updateGlobeVertexNormal(array: SymbolGlobeExtArray, vertexIdx: number, normX: number, normY: number, normZ: number) {
    // Modify float32 array directly. 20 bytes per entry, 3xInt16 for position, 3xfloat32 for normal
    const offset = vertexIdx * 5 + 2;
    array.float32[offset + 0] = normX;
    array.float32[offset + 1] = normY;
    array.float32[offset + 2] = normZ;
}

const addOrientationVertex = (orientationArray: SymbolOrientationArray, numVertices: number, orientedXAxis: vec3, orientedYAxis: vec3) => {
    for (let i = 0; i < numVertices; i++) {
        orientationArray.emplaceBack(orientedXAxis[0], orientedXAxis[1], orientedXAxis[2], orientedYAxis[0], orientedYAxis[1], orientedYAxis[2]);
    }
};

function addDynamicAttributes(dynamicLayoutVertexArray: StructArray, x: number, y: number, z: number, angle: number) {
    dynamicLayoutVertexArray.emplaceBack(x, y, z, angle);
    dynamicLayoutVertexArray.emplaceBack(x, y, z, angle);
    dynamicLayoutVertexArray.emplaceBack(x, y, z, angle);
    dynamicLayoutVertexArray.emplaceBack(x, y, z, angle);
}

function containsRTLText(formattedText: Formatted): boolean {
    for (const section of formattedText.sections) {
        if (stringContainsRTLText(section.text)) {
            return true;
        }
    }
    return false;
}

// Number of uint16 values per vertex in SymbolLayoutArray.
// Layout: [tileAnchorX, tileAnchorY, ox, oy, tx, ty, aSizeX, aSizeY, pixelOffsetX, pixelOffsetY, minFontScaleX, minFontScaleY]
const SYMBOL_VERTEX_STRIDE = 12;

export class SymbolBuffers {
    layoutVertexArray: SymbolLayoutArray;
    layoutVertexBuffer: VertexBuffer;

    indexArray: TriangleIndexArray;
    indexBuffer: IndexBuffer;

    programConfigurations: ProgramConfigurationSet<SymbolStyleLayer>;
    segments: SegmentVector;

    dynamicLayoutVertexArray: SymbolDynamicLayoutArray;
    dynamicLayoutVertexBuffer: VertexBuffer;

    opacityVertexArray: SymbolOpacityArray;
    opacityVertexBuffer: VertexBuffer;

    zOffsetVertexArray: ZOffsetVertexArray;
    zOffsetVertexBuffer: VertexBuffer;

    orientationVertexArray: SymbolOrientationArray;
    orientationVertexBuffer: VertexBuffer;

    iconTransitioningVertexArray: SymbolIconTransitioningArray;
    iconTransitioningVertexBuffer: VertexBuffer | null | undefined;

    globeExtVertexArray: SymbolGlobeExtArray;
    globeExtVertexBuffer: VertexBuffer | null | undefined;

    placedSymbolArray: PlacedSymbolArray;

    symbolInstanceIndices: number[];

    uboBinder: SymbolPropertyBinderUBO | null;
    featureIdArray: StructArrayLayout1f4 | null | undefined;
    featureIdBuffer: VertexBuffer | null;

    cachedBatchIndices: number[] | null;
    cachedBatchSegments: Map<number, SegmentVector> | null;

    constructor(programConfigurations: ProgramConfigurationSet<SymbolStyleLayer>) {
        this.layoutVertexArray = new SymbolLayoutArray();
        this.indexArray = new TriangleIndexArray();
        this.programConfigurations = programConfigurations;
        this.segments = new SegmentVector();
        this.dynamicLayoutVertexArray = new SymbolDynamicLayoutArray();
        this.opacityVertexArray = new SymbolOpacityArray();
        this.placedSymbolArray = new PlacedSymbolArray();
        this.iconTransitioningVertexArray = new SymbolIconTransitioningArray();
        this.globeExtVertexArray = new SymbolGlobeExtArray();
        this.zOffsetVertexArray = new ZOffsetVertexArray();
        this.orientationVertexArray = new SymbolOrientationArray();
        this.symbolInstanceIndices = [];
        this.uboBinder = null;
        this.featureIdArray = new StructArrayLayout1f4();
        this.featureIdBuffer = null;
        this.cachedBatchIndices = null;
        this.cachedBatchSegments = null;
    }

    isEmpty(): boolean {
        return this.layoutVertexArray.length === 0 &&
            this.indexArray.length === 0 &&
            this.dynamicLayoutVertexArray.length === 0 &&
            this.opacityVertexArray.length === 0 &&
            this.iconTransitioningVertexArray.length === 0;
    }

    /**
     * Get cached batch grouping for UBO rendering.
     * Computes and caches the grouping on first call, then reuses cached result.
     *
     * Note: Takes a SegmentVector parameter to detect if we're rendering all segments
     * or just a subset (e.g., in sort-key path where segments are rendered individually).
     * Cache is only used when rendering all segments.
     */
    getBatchGrouping(segments: SegmentVector): {batchIndices: number[]; batchSegments: Map<number, SegmentVector>} {
        // Detect if we're rendering individual segments (sort-key path) vs all segments
        // In sort-key path, each segment is wrapped in new SegmentVector([segment])
        const isRenderingAllSegments = segments.get().length === this.segments.get().length;

        if (isRenderingAllSegments && this.cachedBatchIndices && this.cachedBatchSegments) {
            return {
                batchIndices: this.cachedBatchIndices,
                batchSegments: this.cachedBatchSegments
            };
        }

        // Compute batch grouping from the provided segments (not this.segments)
        // This ensures we respect sort-key reordering when present
        const segmentsByBatch = new Map<number, Array<typeof segments.segments[0]>>();
        for (const segment of segments.get()) {
            const batchIndex = segment.batchIndex !== undefined ? segment.batchIndex : 0;
            if (!segmentsByBatch.has(batchIndex)) {
                segmentsByBatch.set(batchIndex, []);
            }
            segmentsByBatch.get(batchIndex).push(segment);
        }

        // Sort batch indices and create SegmentVectors
        const batchIndices = Array.from(segmentsByBatch.keys()).sort((a, b) => a - b);
        const batchSegments = new Map<number, SegmentVector>();
        for (const batchIndex of batchIndices) {
            const segs = segmentsByBatch.get(batchIndex);
            batchSegments.set(batchIndex, new SegmentVector(segs));
        }

        // Only cache when rendering all segments
        if (isRenderingAllSegments) {
            this.cachedBatchIndices = batchIndices;
            this.cachedBatchSegments = batchSegments;
        }

        return {batchIndices, batchSegments};
    }

    // Returns a copy of [numVertices] vertices starting at [offset] as a Uint16Array snapshot.
    // Use restoreSymbolVertexData to write it back.
    snapshotSymbolVertexData(offset: number, numVertices: number): Uint16Array {
        assert(offset >= 0 && offset < this.layoutVertexArray.length, 'Invalid vertex offset');
        const start = offset * SYMBOL_VERTEX_STRIDE;
        return this.layoutVertexArray.uint16.slice(start, start + numVertices * SYMBOL_VERTEX_STRIDE);
    }

    // Writes a snapshot (produced by snapshotSymbolVertexData) back into the vertex array at [offset].
    restoreSymbolVertexData(offset: number, snapshot: Uint16Array) {
        assert(
            offset >= 0 && offset * SYMBOL_VERTEX_STRIDE + snapshot.length <= this.layoutVertexArray.uint16.length,
            'Invalid restore offset'
        );
        this.layoutVertexArray.uint16.set(snapshot, offset * SYMBOL_VERTEX_STRIDE);
    }

    updateSymbolVertexData(vertexIndex: number, anchorX: number, anchorY: number, newOx: number, newOy: number, newTx: number, newTy: number, newSizeX: number, newSizeY: number, pixelOffsetX: number, pixelOffsetY: number, minFontScaleX: number, minFontScaleY: number) {
        assert(vertexIndex >= 0 && vertexIndex < this.layoutVertexArray.length, 'Invalid vertex start index');

        const uint16Array = this.layoutVertexArray.uint16;
        const baseOffset = vertexIndex * SYMBOL_VERTEX_STRIDE;
        uint16Array[baseOffset] = anchorX;
        uint16Array[baseOffset + 1] = anchorY;
        uint16Array[baseOffset + 2] = newOx;
        uint16Array[baseOffset + 3] = newOy;
        uint16Array[baseOffset + 4] = newTx;
        uint16Array[baseOffset + 5] = newTy;
        uint16Array[baseOffset + 6] = newSizeX;
        uint16Array[baseOffset + 7] = newSizeY;
        uint16Array[baseOffset + 8] = pixelOffsetX;
        uint16Array[baseOffset + 9] = pixelOffsetY;
        uint16Array[baseOffset + 10] = minFontScaleX;
        uint16Array[baseOffset + 11] = minFontScaleY;
    }

    upload(context: Context, dynamicIndexBuffer: boolean, upload?: boolean, update?: boolean, createZOffsetBuffer?: boolean, hasAppearances?: boolean) {
        if (this.isEmpty()) {
            return;
        }

        if (upload) {
            // Invalidate batch grouping cache: index buffer is being replaced, so
            // any cached segment→batch mapping is stale.
            this.cachedBatchIndices = null;
            this.cachedBatchSegments = null;

            this.layoutVertexBuffer = context.createVertexBuffer(this.layoutVertexArray, symbolLayoutAttributes.members, !!hasAppearances);
            this.indexBuffer = context.createIndexBuffer(this.indexArray, dynamicIndexBuffer);
            this.dynamicLayoutVertexBuffer = context.createVertexBuffer(this.dynamicLayoutVertexArray, dynamicLayoutAttributes.members, true);
            this.opacityVertexBuffer = context.createVertexBuffer(this.opacityVertexArray, shaderOpacityAttributes, true);
            if (this.iconTransitioningVertexArray.length > 0) {
                this.iconTransitioningVertexBuffer = context.createVertexBuffer(this.iconTransitioningVertexArray, iconTransitioningAttributes.members, true);
            }
            if (this.globeExtVertexArray.length > 0) {
                this.globeExtVertexBuffer = context.createVertexBuffer(this.globeExtVertexArray, symbolGlobeExtAttributes.members, true);
            }
            if (!this.zOffsetVertexBuffer && (this.zOffsetVertexArray.length > 0 || !!createZOffsetBuffer)) {
                this.zOffsetVertexBuffer = context.createVertexBuffer(this.zOffsetVertexArray, zOffsetAttributes.members, true);
            }
            if (!this.orientationVertexBuffer && this.orientationVertexArray && this.orientationVertexArray.length > 0) {
                this.orientationVertexBuffer = context.createVertexBuffer(this.orientationVertexArray, orientationAttributes.members, true);
                assert(this.orientationVertexBuffer.length === this.layoutVertexBuffer.length);
            }
            // This is a performance hack so that we can write to opacityVertexArray with uint32s
            // even though the shaders read uint8s
            this.opacityVertexBuffer.itemSize = 1;

            if (this.featureIdArray && this.featureIdArray.length > 0) {
                this.featureIdBuffer = context.createVertexBuffer(this.featureIdArray, featureIdAttributes.members, false);
            }
        }
        if (upload || update) {
            this.programConfigurations.upload(context);

            if (this.uboBinder) {
                this.uboBinder.upload(context);
            }
        }
    }

    destroy() {
        if (!this.layoutVertexBuffer) return;

        this.layoutVertexBuffer.destroy();
        this.indexBuffer.destroy();
        this.programConfigurations.destroy();
        this.segments.destroy();
        this.dynamicLayoutVertexBuffer.destroy();
        this.opacityVertexBuffer.destroy();
        if (this.iconTransitioningVertexBuffer) {
            this.iconTransitioningVertexBuffer.destroy();
        }
        if (this.globeExtVertexBuffer) {
            this.globeExtVertexBuffer.destroy();
        }
        if (this.zOffsetVertexBuffer) {
            this.zOffsetVertexBuffer.destroy();
        }
        if (this.orientationVertexBuffer) {
            this.orientationVertexBuffer.destroy();
        }

        if (this.featureIdBuffer) {
            this.featureIdBuffer.destroy();
        }
        if (this.uboBinder) {
            this.uboBinder.destroy();
        }
    }
}

register(SymbolBuffers, 'SymbolBuffers', {omit: ['cachedBatchIndices', 'cachedBatchSegments']});

class CollisionBuffers {
    layoutVertexArray: StructArray;
    layoutAttributes: Array<StructArrayMember>;
    layoutVertexBuffer: VertexBuffer;

    indexArray: TriangleIndexArray | LineIndexArray;
    indexBuffer: IndexBuffer;

    segments: SegmentVector;

    collisionVertexArray: CollisionVertexArray;
    collisionVertexBuffer: VertexBuffer;

    collisionVertexArrayExt: CollisionVertexExtArray;
    collisionVertexBufferExt: VertexBuffer;

    constructor(LayoutArray: Class<StructArray>,
                layoutAttributes: Array<StructArrayMember>,
                IndexArray: Class<TriangleIndexArray | LineIndexArray>) {
        this.layoutVertexArray = new LayoutArray();
        this.layoutAttributes = layoutAttributes;
        this.indexArray = new IndexArray();
        this.segments = new SegmentVector();
        this.collisionVertexArray = new CollisionVertexArray();
        this.collisionVertexArrayExt = new CollisionVertexExtArray();
    }

    upload(context: Context) {
        this.layoutVertexBuffer = context.createVertexBuffer(this.layoutVertexArray, this.layoutAttributes);
        this.indexBuffer = context.createIndexBuffer(this.indexArray);
        this.collisionVertexBuffer = context.createVertexBuffer(this.collisionVertexArray, collisionVertexAttributes.members, true);
        this.collisionVertexBufferExt = context.createVertexBuffer(this.collisionVertexArrayExt, collisionVertexAttributesExt.members, true);
    }

    destroy() {
        if (!this.layoutVertexBuffer) return;
        this.layoutVertexBuffer.destroy();
        this.indexBuffer.destroy();
        this.segments.destroy();
        this.collisionVertexBuffer.destroy();
        this.collisionVertexBufferExt.destroy();
    }
}

register(CollisionBuffers, 'CollisionBuffers');

/**
 * Unlike other buckets, which simply implement #addFeature with type-specific
 * logic for (essentially) triangulating feature geometries, SymbolBucket
 * requires specialized behavior:
 *
 * 1. WorkerTile#parse(), the logical owner of the bucket creation process,
 *    calls SymbolBucket#populate(), which resolves text and icon tokens on
 *    each feature, adds each glyphs and symbols needed to the passed-in
 *    collections options.glyphDependencies and options.iconDependencies, and
 *    stores the feature data for use in subsequent step (this.features).
 *
 * 2. WorkerTile asynchronously requests from the main thread all of the glyphs
 *    and icons needed (by this bucket and any others). When glyphs and icons
 *    have been received, the WorkerTile creates a CollisionIndex and invokes:
 *
 * 3. performSymbolLayout(bucket, stacks, icons) perform texts shaping and
 *    layout on a Symbol Bucket. This step populates:
 *      `this.symbolInstances`: metadata on generated symbols
 *      `collisionBoxArray`: collision data for use by foreground
 *      `this.text`: SymbolBuffers for text symbols
 *      `this.icons`: SymbolBuffers for icons
 *      `this.iconCollisionBox`: Debug SymbolBuffers for icon collision boxes
 *      `this.textCollisionBox`: Debug SymbolBuffers for text collision boxes
 *    The results are sent to the foreground for rendering
 *
 * 4. Placement.updateBucketOpacities() is run on the foreground,
 *    and uses the CollisionIndex along with current camera settings to determine
 *    which symbols can actually show on the map. Collided symbols are hidden
 *    using a dynamic "OpacityVertexArray".
 *
 * @private
 */
class SymbolBucket implements Bucket {
    static addDynamicAttributes: typeof addDynamicAttributes;

    collisionBoxArray: CollisionBoxArray;
    zoom: number;
    lut: LUT | null;
    overscaling: number;
    layers: Array<SymbolStyleLayer>;
    layerIds: Array<string>;
    stateDependentLayers: Array<SymbolStyleLayer>;
    stateDependentLayerIds: Array<string>;

    index: number;
    sdfIcons: boolean;
    iconsInText: boolean;
    iconsNeedLinear: boolean;
    bucketInstanceId: number;
    justReloaded: boolean;
    hasPattern: boolean;
    fullyClipped: boolean;

    textSizeData: SizeData;
    iconSizeData: SizeData;

    glyphOffsetArray: GlyphOffsetArray;
    lineVertexArray: SymbolLineVertexArray;
    features: Array<SymbolFeature>;
    featureAppearanceData: Map<number, AppearanceFeatureData>;
    symbolInstances: SymbolInstanceArray;
    hasAnySecondaryIcon: boolean;
    collisionArrays: Array<CollisionArrays>;
    sortKeyRanges: Array<SortKeyRange>;
    pixelRatio: number;
    tilePixelRatio: number;
    compareText: {
        [_: string]: Array<Point>;
    };
    fadeStartTime: number;
    sortFeaturesByKey: boolean;
    sortFeaturesByY: boolean;
    canOverlap: boolean;
    sortedAngle: number;
    featureSortOrder: Array<number>;

    // Cache for per-segment sorting optimization (symbols never change batches)
    symbolsByBatch: Map<number, Array<{index: number; rotatedY: number; featureIndex: number}>> | null;

    collisionCircleArray: Array<number>;
    placementInvProjMatrix: mat4;
    placementViewportMatrix: mat4;

    text: SymbolBuffers;
    icon: SymbolBuffers;
    textCollisionBox: CollisionBuffers;
    iconCollisionBox: CollisionBuffers;
    uploaded: boolean;
    sourceLayerIndex: number;
    sourceID: string;
    symbolInstanceIndexes: Array<number>;
    writingModes: Array<number>;
    allowVerticalPlacement: boolean;
    hasRTLText: boolean;
    projection: ProjectionSpecification;
    projectionInstance: Projection | null | undefined;
    hasAnyIconTextFit: boolean;
    hasAnyZOffset: boolean;
    symbolInstanceIndexesSortedZOffset: Array<number>;
    zOffsetSortDirty: boolean;
    zOffsetBuffersNeedUpload: boolean;

    elevationType: ElevationType;
    elevationFeatures: Array<ElevationFeature>;
    elevationFeatureIdToIndex: Map<number, number>;
    elevationStateComplete: boolean;

    activeReplacements: Array<Region>;
    replacementUpdateTime: number;

    worldview: string;
    localizable: boolean;
    maxUniformBufferBindings: number | null | undefined;
    maxUniformBlockSizeDwords: number | null | undefined;
    disableSymbolUBO: boolean | null | undefined;
    iconAtlasPositions: ImagePositionMap;
    hasAppearances: boolean | null;
    featureAppearances: FeatureAppearances | null;

    constructor(options: BucketParameters<SymbolStyleLayer>) {
        this.collisionBoxArray = options.collisionBoxArray;
        this.zoom = options.zoom;
        this.overscaling = options.overscaling;
        this.layers = options.layers;
        this.layerIds = this.layers.map(layer => layer.fqid);
        this.index = options.index;
        this.pixelRatio = options.pixelRatio;
        this.sourceLayerIndex = options.sourceLayerIndex;
        this.hasPattern = false;
        this.hasRTLText = false;
        this.fullyClipped = false;
        this.hasAnyIconTextFit = false;
        this.sortKeyRanges = [];

        this.collisionCircleArray = [];
        this.placementInvProjMatrix = mat4.identity([]);
        this.placementViewportMatrix = mat4.identity([]);

        const layer = this.layers[0];
        const unevaluatedLayoutValues = layer._unevaluatedLayout._values;

        this.worldview = options.worldview;
        this.localizable = options.localizable;
        this.maxUniformBufferBindings = options.maxUniformBufferBindings;
        this.maxUniformBlockSizeDwords = options.maxUniformBlockSizeDwords;
        this.disableSymbolUBO = options.disableSymbolUBO;

        this.textSizeData = getSizeData(this.zoom, unevaluatedLayoutValues['text-size'], this.worldview, options.availableImages);

        this.iconSizeData = getSizeData(this.zoom, unevaluatedLayoutValues['icon-size'], this.worldview, options.availableImages);

        const layout = this.layers[0].layout;
        const sortKey = layout.get('symbol-sort-key');
        const zOrder = layout.get('symbol-z-order');

        this.lut = options.lut;

        this.canOverlap =
            layout.get('text-allow-overlap') ||
            layout.get('icon-allow-overlap') ||
            layout.get('text-ignore-placement') ||
            layout.get('icon-ignore-placement');

        this.sortFeaturesByKey = zOrder !== 'viewport-y' && sortKey.constantOr(1) !== undefined;
        const zOrderByViewportY = zOrder === 'viewport-y' || (zOrder === 'auto' && !this.sortFeaturesByKey);
        this.sortFeaturesByY = zOrderByViewportY && this.canOverlap;

        this.writingModes = layout.get('text-writing-mode').map(wm => WritingMode[wm]);

        this.stateDependentLayerIds = this.layers.filter((l) => l.isStateDependent()).map((l) => l.id);

        this.sourceID = options.sourceID;
        this.projection = options.projection;
        this.hasAnyZOffset = false;
        this.zOffsetSortDirty = false;

        this.zOffsetBuffersNeedUpload = false;

        this.elevationType = 'none';
        this.elevationStateComplete = false;

        this.activeReplacements = [];
        this.replacementUpdateTime = 0;
        this.hasAnySecondaryIcon = false;
        this.hasAppearances = null;
        this.featureAppearances = null;
        this.featureAppearanceData = new Map();
    }

    hasAnyAppearanceLayoutProperty(properties: keyof AppearanceProps | (keyof AppearanceProps)[]): boolean {
        const layer = this.layers[0];
        const appearances = layer.getAppearances();
        if (!appearances || appearances.length === 0) {
            return false;
        }

        const propertiesToCheck = Array.isArray(properties) ? properties : [properties];
        return appearances.some(appearance => {
            return propertiesToCheck.some(propertyName => appearance.hasLayoutProperty(propertyName));
        });
    }

    getAppearanceFeatureData(featureIndex: number): AppearanceFeatureData | undefined {
        return this.featureAppearanceData.get(featureIndex);
    }

    createArrays() {
        this.text = new SymbolBuffers(
            new ProgramConfigurationSet(this.layers, {zoom: this.zoom, lut: this.lut}, (property) => {
                return property.startsWith('text') || property.startsWith('symbol');
            })
        );
        this.icon = new SymbolBuffers(
            new ProgramConfigurationSet(this.layers, {zoom: this.zoom, lut: this.lut}, (property) => {
                return property.startsWith('icon') || property.startsWith('symbol');
            })
        );

        if (!this.disableSymbolUBO) {
            this.text.uboBinder = new SymbolPropertyBinderUBO(this.layers[0], this.zoom, this.lut, true, '', this.maxUniformBufferBindings, this.maxUniformBlockSizeDwords);
            this.icon.uboBinder = new SymbolPropertyBinderUBO(this.layers[0], this.zoom, this.lut, false, '', this.maxUniformBufferBindings, this.maxUniformBlockSizeDwords);
        }

        this.glyphOffsetArray = new GlyphOffsetArray();
        this.lineVertexArray = new SymbolLineVertexArray();
        this.symbolInstances = new SymbolInstanceArray();
    }

    calculateGlyphDependencies(text: string, stack: Record<number, boolean>, textAlongLine: boolean, allowVerticalPlacement: boolean, doesAllowVerticalWritingMode: boolean) {
        for (const char of text) {
            const codePoint = char.codePointAt(0);
            if (codePoint === undefined) break;
            stack[codePoint] = true;
            if (allowVerticalPlacement && doesAllowVerticalWritingMode && codePoint <= 65535) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const verticalChar = verticalizedCharacterMap[char];
                if (verticalChar) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                    stack[verticalChar.charCodeAt(0)] = true;
                }
            }
        }
    }

    calculateEffectiveAppearanceIconSize(
        activeAppearance: SymbolAppearance,
        currentZoom: number,
        evaluationFeature: EvaluationFeature,
        canonical: CanonicalTileID,
        availableImages: Array<ImageId>,
        iconScaleFactor: number,
        layoutIconSize: number
    ): number {
        // If appearance doesn't define icon-size, use layout value
        if (!activeAppearance.hasLayoutProperty('icon-size')) {
            return layoutIconSize * iconScaleFactor;
        }

        let effectiveIconSize = 1;
        const unevaluatedIconSize = activeAppearance.getUnevaluatedLayoutProperty('icon-size') as PropertyValue<number, PossiblyEvaluatedPropertyValue<number>>;
        const iconSizeData = getSizeData(this.zoom, unevaluatedIconSize, this.worldview, availableImages);
        const sizeValue = evaluateSizeForZoom(iconSizeData, currentZoom);
        if (iconSizeData.kind === 'constant' || iconSizeData.kind === 'camera') {
            effectiveIconSize = sizeValue.uSize;
        }
        if (iconSizeData.kind === 'composite') {
            const {minZoom, maxZoom} = iconSizeData;
            const possibleSizeMin = unevaluatedIconSize.possiblyEvaluate(new EvaluationParameters(minZoom, {worldview: this.worldview}), canonical);
            const possibleSizeMax = unevaluatedIconSize.possiblyEvaluate(new EvaluationParameters(maxZoom, {worldview: this.worldview}), canonical);
            const sizeMin = possibleSizeMin.evaluate(evaluationFeature, {}, canonical, availableImages);
            const sizeMax = possibleSizeMax.evaluate(evaluationFeature, {}, canonical, availableImages);
            effectiveIconSize = sizeMin + (sizeMax - sizeMin) * sizeValue.uSizeT;
        }
        if (iconSizeData.kind === 'source') {
            const possibleSize = unevaluatedIconSize.possiblyEvaluate(new EvaluationParameters(this.zoom, {worldview: this.worldview}), canonical);
            effectiveIconSize = possibleSize.evaluate(evaluationFeature, {}, canonical, availableImages);
        }

        return effectiveIconSize * iconScaleFactor;
    }

    updateFootprints(_id: UnwrappedTileID, _footprints: Array<TileFootprint>) {
    }

    updateReplacement(coord: OverscaledTileID, source: ReplacementSource): boolean {
        // Replacement has to be re-checked if the source has been updated since last time
        if (source.updateTime === this.replacementUpdateTime) {
            return false;
        }
        this.replacementUpdateTime = source.updateTime;

        // Check if replacements have changed
        const newReplacements = source.getReplacementRegionsForTile(coord.toUnwrapped(), true);
        if (regionsEquals(this.activeReplacements, newReplacements)) {
            return false;
        }
        this.activeReplacements = newReplacements;
        return true;
    }

    getResolvedImageFromTokens(tokens: string) {
        // The type of tokens comes from layer.getValueAndResolveTokens
        // But that type is wrong because we wouldn't need to do
        // this otherwise
        if (typeof tokens === "string") {
            return ResolvedImage.build(tokens);
        }
        return tokens;
    }

    populate(features: Array<IndexedFeature>, options: PopulateParameters, canonical: CanonicalTileID, tileTransform: TileTransform) {
        const layer = this.layers[0];
        const layout = layer.layout;
        const isGlobe = this.projection.name === 'globe';

        const textFont = layout.get('text-font');
        const textField = layout.get('text-field');
        const iconImage = layout.get('icon-image');
        const [iconSizeScaleRangeMin, iconSizeScaleRangeMax] = layout.get('icon-size-scale-range');
        const iconScaleFactor = clamp(options.scaleFactor || 1, iconSizeScaleRangeMin, iconSizeScaleRangeMax);
        const [textSizeScaleRangeMin, textSizeScaleRangeMax] = layout.get('text-size-scale-range');
        const textScaleFactor = clamp(options.scaleFactor || 1, textSizeScaleRangeMin, textSizeScaleRangeMax);
        const hasText =

            (textField.value.kind !== 'constant' ||

                (textField.value.value instanceof Formatted && !textField.value.value.isEmpty()) ||

                textField.value.value.toString().length > 0) &&

            (textFont.value.kind !== 'constant' || textFont.value.value.length > 0);
        // we should always resolve the icon-image value if the property was defined in the style
        // this allows us to fire the styleimagemissing event if image evaluation returns null
        // the only way to distinguish between null returned from a coalesce statement with no valid images
        // and null returned because icon-image wasn't defined is to check whether or not iconImage.parameters is an empty object

        const hasIcon = iconImage.value.kind !== 'constant' || !!iconImage.value.value || Object.keys(iconImage.parameters).length > 0;
        const hasAppearanceIcons = this.hasAnyAppearanceLayoutProperty('icon-image');

        const symbolSortKey = layout.get('symbol-sort-key');

        this.features = [];
        this.featureAppearanceData = new Map();

        if (!hasText && !hasIcon && !hasAppearanceIcons) {
            return;
        }

        const icons = options.iconDependencies;
        const stacks = options.glyphDependencies;
        const availableImages = options.availableImages;
        const globalProperties = new EvaluationParameters(this.zoom, {worldview: this.worldview, activeFloors: options.activeFloors});
        const addImageVariantToIcons = (variant: ImageVariant) => {
            const variantId = variant.id.toString();
            if (icons.has(variantId)) {
                icons.get(variantId).push(variant);
            } else {
                icons.set(variantId, [variant]);
            }
        };

        for (const indexedFeature of features) {
            const {feature, id, index, sourceLayerIndex} = indexedFeature;

            const needGeometry = layer._featureFilter.needGeometry;
            const evaluationFeature = toEvaluationFeature(feature, needGeometry);
            if (!layer._featureFilter.filter(globalProperties, evaluationFeature, canonical)) {
                continue;
            }

            if (!needGeometry) evaluationFeature.geometry = loadGeometry(feature, canonical, tileTransform);

            if (isGlobe && feature.type !== 1 && canonical.z <= 5) {
                // Resample long lines and polygons in globe view so that their length wont exceed ~0.19 radians (360/32 degrees).
                // Otherwise lines could clip through the globe as the resolution is not enough to represent curved paths.
                // The threshold value follows subdivision size used with fill extrusions
                const geom = evaluationFeature.geometry;

                // cos(11.25 degrees) = 0.98078528056
                const cosAngleThreshold = 0.98078528056;
                const predicate = (a: Point, b: Point) => {
                    const v0 = tileCoordToECEF(a.x, a.y, canonical, 1);
                    const v1 = tileCoordToECEF(b.x, b.y, canonical, 1);
                    return vec3.dot(v0, v1) < cosAngleThreshold;
                };

                for (let i = 0; i < geom.length; i++) {
                    geom[i] = resamplePred(geom[i], predicate);
                }
            }

            let text: Formatted | undefined;
            if (hasText) {
                // Expression evaluation will automatically coerce to Formatted
                // but plain string token evaluation skips that pathway so do the
                // conversion here.
                const resolvedTokens = layer.getValueAndResolveTokens('text-field', evaluationFeature, canonical, availableImages);
                const formattedText = Formatted.factory(resolvedTokens);
                if (containsRTLText(formattedText)) {
                    this.hasRTLText = true;
                }
                if (
                    !this.hasRTLText || // non-rtl text so can proceed safely
                    getRTLTextPluginStatus() === 'unavailable' || // We don't intend to lazy-load the rtl text plugin, so proceed with incorrect shaping
                    (this.hasRTLText && globalRTLTextPlugin.isParsed()) // Use the rtlText plugin to shape text
                ) {
                    text = transformText(formattedText, layer, evaluationFeature);
                }
            }

            let icon: ResolvedImage | null | undefined;
            if (hasIcon) {
                // Expression evaluation will automatically coerce to Image
                // but plain string token evaluation skips that pathway so do the
                // conversion here.
                const resolvedTokens = layer.getValueAndResolveTokens('icon-image', evaluationFeature, canonical, availableImages);
                icon = this.getResolvedImageFromTokens(resolvedTokens);
            }

            const symbolLayer = this.layers[0];
            let usesAppearanceIconAsFallback = false;
            if (!icon && hasAppearanceIcons) {
                const appearances = symbolLayer.getAppearances();
                for (const appearance of appearances) {
                    const iconImage = appearance.getLayoutProperty('icon-image');
                    if (iconImage) {
                        const resolvedTokens = symbolLayer.getAppearanceValueAndResolveTokens(appearance, 'icon-image', evaluationFeature, canonical, availableImages);
                        if (resolvedTokens) {
                            icon = this.getResolvedImageFromTokens(resolvedTokens as string);
                            usesAppearanceIconAsFallback = true;
                            break;
                        }
                    }
                }
            }

            if (!text && !icon) {
                continue;
            }
            const sortKey = this.sortFeaturesByKey ?

                symbolSortKey.evaluate(evaluationFeature, {}, canonical) :
                undefined;

            const symbolFeature: SymbolFeature = {
                id,
                text,
                icon,
                index,
                sourceLayerIndex,
                geometry: evaluationFeature.geometry,
                properties: feature.properties,
                type: vectorTileFeatureTypes[feature.type],
                sortKey
            };
            this.features.push(symbolFeature);

            // Store minimal data needed for appearance evaluation
            // Use the promoted ID from IndexedFeature (resolved in worker thread) instead of raw feature.id
            this.featureAppearanceData.set(index, {
                id, // This is already the promoted ID from IndexedFeature
                properties: feature.properties,
                usesAppearanceIconAsPlaceholder: usesAppearanceIconAsFallback,
                isUsingAppearanceIconVertexData: false,
                isUsingAppearanceTextVertexData: false,
                layoutBasedIconVertexData: new Uint16Array(0),
                layoutBasedTextVertexData: new Uint16Array(0),
                activeAppearanceIndex: UNINITIALIZED_APPEARANCE_INDEX
            });

            if (icon) {
                const unevaluatedLayoutValues = symbolLayer._unevaluatedLayout._values;
                const {iconPrimary, iconSecondary} = getScaledImageVariant(icon, this.iconSizeData, unevaluatedLayoutValues['icon-size'], canonical, this.zoom, symbolFeature, this.pixelRatio, iconScaleFactor, this.worldview, availableImages);
                addImageVariantToIcons(iconPrimary);

                if (iconSecondary) {
                    this.hasAnySecondaryIcon = true;
                    addImageVariantToIcons(iconSecondary);
                }
            }

            const appearances = symbolLayer.getAppearances();
            if (appearances.length !== 0) {
                appearances.forEach(a => {
                    const iconImage = a.getLayoutProperty('icon-image');
                    if (!iconImage) return;
                    const iconPrimary = this.getCombinedIconPrimary(a, symbolLayer, evaluationFeature, canonical, availableImages, symbolFeature, iconScaleFactor);
                    if (!iconPrimary) return;
                    addImageVariantToIcons(iconPrimary);
                });
            }

            if (text) {
                const fontStack = textFont.evaluate(evaluationFeature, {}, canonical).join(',');
                const textAlongLine = layout.get('text-rotation-alignment') === 'map' && layout.get('symbol-placement') !== 'point';
                this.allowVerticalPlacement = this.writingModes && this.writingModes.indexOf(WritingMode.vertical) >= 0;
                for (const section of text.sections) {
                    if (!section.image) {
                        const doesAllowVerticalWritingMode = allowsVerticalWritingMode(text.toString());
                        const sectionFont = section.fontStack || fontStack;
                        const sectionStack: Record<number, boolean> = stacks[sectionFont] = stacks[sectionFont] || {};
                        this.calculateGlyphDependencies(section.text, sectionStack, textAlongLine, this.allowVerticalPlacement, doesAllowVerticalWritingMode);
                    } else {
                        const imagePrimary = section.image.getPrimary().scaleSelf(this.pixelRatio * textScaleFactor);
                        const imagePrimaryId = imagePrimary.id.toString();
                        const primaryIcons = icons.get(imagePrimaryId) || [];
                        primaryIcons.push(imagePrimary);
                        icons.set(imagePrimaryId, primaryIcons);
                    }
                }
            }
        }

        if (layout.get('symbol-placement') === 'line') {
            // Merge adjacent lines with the same text to improve labelling.
            // It's better to place labels on one long line than on many short segments.
            this.features = mergeLines(this.features);
        }

        if (layout.get('symbol-elevation-reference') === 'hd-road-markup') {
            this.elevationType = 'road';
            if (options.elevationFeatures) {
                if (!this.elevationFeatures && options.elevationFeatures.length > 0) {
                    this.elevationFeatures = [];
                    this.elevationFeatureIdToIndex = new Map<number, number>();
                }
                for (const elevationFeature of options.elevationFeatures) {
                    this.elevationFeatureIdToIndex.set(elevationFeature.id, this.elevationFeatures.length);
                    this.elevationFeatures.push(elevationFeature);
                }
            }
        } else if (layout.get('symbol-z-elevate')) {
            this.elevationType = 'offset';
        }
        if (this.elevationType !== 'none') {
            this.zOffsetBuffersNeedUpload = true;
        }

        if (this.sortFeaturesByKey) {
            this.features.sort((a, b) => {
                // a.sortKey is always a number when sortFeaturesByKey is true
                return (a.sortKey) - (b.sortKey);
            });
        }
    }

    getCombinedIconPrimary(appearance: SymbolAppearance, layer: SymbolStyleLayer, evaluationFeature: EvaluationFeature, canonical: CanonicalTileID, availableImages: ImageId[],
        symbolFeature: SymbolFeature, iconScaleFactor: number
    ): ImageVariant | undefined {
        let icon: ResolvedImage;
        let iconPrimary: ImageVariant;
        if (appearance.hasLayoutProperty('icon-image')) {
            const resolvedTokens = layer.getAppearanceValueAndResolveTokens(appearance, 'icon-image', evaluationFeature, canonical, availableImages);
            icon = this.getResolvedImageFromTokens(resolvedTokens as string);
        } else {
            const resolvedTokens = layer.getValueAndResolveTokens('icon-image', evaluationFeature, canonical, availableImages);
            icon = this.getResolvedImageFromTokens(resolvedTokens);
        }

        if (icon) {
            const unevaluatedIconSize = (appearance.hasLayoutProperty('icon-size') ?
                appearance.getUnevaluatedLayoutProperty('icon-size') :
                layer._unevaluatedLayout._values['icon-size']) as PropertyValue<number, PossiblyEvaluatedPropertyValue<number>>;
            const iconSizeData = getSizeData(this.zoom, unevaluatedIconSize, this.worldview, availableImages);
            const imageVariant = getScaledImageVariant(icon, iconSizeData, unevaluatedIconSize, canonical, this.zoom, symbolFeature, this.pixelRatio, iconScaleFactor, this.worldview, availableImages);
            iconPrimary = imageVariant.iconPrimary;
        }

        return iconPrimary;
    }

    private updateSymbolInstanceIconVertices(
        symbolInstance: SymbolInstance,
        featureData: AppearanceFeatureData,
        activeAppearanceIndex: number,
        evaluationFeature: EvaluationFeature,
        vertexOffset: number,
        ctx: AppearanceIconUpdateContext,
    ): {vertexOffsetDelta: number; hasChanges: boolean} {
        const {canonical, availableImages, globalProperties, layer, iconScaleFactor, featureState, layoutIconOffset, layoutIconSize, layoutIconRotate} = ctx;
        if (symbolInstance.placedIconSymbolIndex < 0) {
            return {vertexOffsetDelta: 0, hasChanges: false};
        }

        if (featureData.activeAppearanceIndex === activeAppearanceIndex) {
            return {vertexOffsetDelta: symbolInstance.numIconVertices, hasChanges: false};
        }

        const activeAppearance = activeAppearanceIndex >= 0 ? layer.appearances[activeAppearanceIndex] : null;
        if (activeAppearance) {
            const minimalFeature: SymbolFeature = {
                sortKey: undefined,
                text: undefined,
                icon: null, // Will be resolved in getAppearanceIconPrimary
                index: symbolInstance.featureIndex,
                sourceLayerIndex: symbolInstance.featureIndex,
                geometry: [],
                properties: featureData.properties as Record<string, string | number | boolean>,
                type: 'Point',
                id: featureData.id
            };

            const iconPrimary = this.getCombinedIconPrimary(activeAppearance, layer, evaluationFeature, canonical, availableImages, minimalFeature, iconScaleFactor);
            if (!iconPrimary) return {vertexOffsetDelta: 0, hasChanges: false};

            const primaryImageSerialized = iconPrimary.toString();
            const position = this.iconAtlasPositions && this.iconAtlasPositions.get(primaryImageSerialized);

            if (position) {
                // Get values from appearance and fallback to layout ones
                const {appearanceIconOffset: iconOffset, appearanceIconRotate: iconRotate} = getAppearanceIconValues(activeAppearance, layer, evaluationFeature as SymbolFeature, canonical, layoutIconOffset, layoutIconRotate, layoutIconSize, iconScaleFactor);
                const iconAnchor = layer.layout.get('icon-anchor').evaluate(evaluationFeature, featureState, canonical);
                let shapedIcon = shapeIcon(position, undefined, iconOffset, iconAnchor);

                const isSDFIcon = position.sdf;
                // Get icon-text-fit from layout since we don't support it in appearances
                const iconTextFit = layer.layout.get('icon-text-fit').constantOr('none');

                // Note: This needs to happen after text has been updated in appearances since it can change featureData.textShaping
                // Use featureData.fontScale which converts the glyph-space bounds to pixels at the appearance text-size
                if (iconTextFit !== 'none' && featureData.textShaping && featureData.iconTextFitPadding && featureData.fontScale) {
                    shapedIcon = fitIconToText(shapedIcon, featureData.textShaping, iconTextFit,
                        featureData.iconTextFitPadding, iconOffset, featureData.fontScale);
                }

                // Get appearance-specific icon-size and calculate combined scale factor
                const effectiveIconSize = this.calculateEffectiveAppearanceIconSize(
                    activeAppearance,
                    globalProperties.zoom,
                    evaluationFeature,
                    canonical,
                    availableImages,
                    iconScaleFactor,
                    layoutIconSize
                );
                const newSizeX = 0; // Unused in appearances
                const newSizeY = (Math.min(MAX_PACKED_SIZE, Math.round(effectiveIconSize * SIZE_PACK_FACTOR)) << 1) + 1; // pack isAppearance flag in lowest bit
                // Generate new icon quads with updated texture coordinates and size
                const iconQuads = getIconQuads(shapedIcon, iconRotate, isSDFIcon, iconTextFit !== 'none', iconScaleFactor);

                // Store the layout-based vertex data to restore it later if it's the first time we use an appearance for this feature
                if (!featureData.isUsingAppearanceIconVertexData) {
                    featureData.isUsingAppearanceIconVertexData = true;
                    featureData.layoutBasedIconVertexData = this.icon.snapshotSymbolVertexData(vertexOffset, symbolInstance.numIconVertices);
                }

                // Update vertex data - only update as many quads as were allocated during layout
                const maxQuads = Math.floor(symbolInstance.numIconVertices / 4);
                const quadsToUpdate = Math.min(iconQuads.length, maxQuads);

                let currentVertexOffset = vertexOffset;
                for (let j = 0; j < quadsToUpdate; ++j) {
                    const quad = iconQuads[j];

                    // Since icon-anchor is not supported in appearances we use the first layout quad one
                    // Note that layout quads might not exist, in that case we use the symbolInstance
                    // tileAnchor.
                    const anchorX = featureData.layoutBasedIconVertexData[0] || symbolInstance.tileAnchorX;
                    const anchorY = featureData.layoutBasedIconVertexData[1] || symbolInstance.tileAnchorY;
                    const pixelOffsetTLX = quad.pixelOffsetTL.x * 16;
                    const pixelOffsetTLY = quad.pixelOffsetTL.y * 16;
                    const pixelOffsetBRX = quad.pixelOffsetBR.x * 16;
                    const pixelOffsetBRY = quad.pixelOffsetBR.y * 16;
                    const minFontScaleX = quad.minFontScaleX * 16;
                    const minFontScaleY = quad.minFontScaleY * 16;

                    this.icon.updateSymbolVertexData(currentVertexOffset, anchorX, anchorY, Math.round(quad.tl.x * 32), Math.round(quad.tl.y * 32), quad.texPrimary.x, quad.texPrimary.y, newSizeX, newSizeY, pixelOffsetTLX, pixelOffsetTLY, minFontScaleX, minFontScaleY);
                    this.icon.updateSymbolVertexData(currentVertexOffset + 1, anchorX, anchorY, Math.round(quad.tr.x * 32), Math.round(quad.tr.y * 32), quad.texPrimary.x + quad.texPrimary.w, quad.texPrimary.y, newSizeX, newSizeY, pixelOffsetBRX, pixelOffsetTLY, minFontScaleX, minFontScaleY);
                    this.icon.updateSymbolVertexData(currentVertexOffset + 2, anchorX, anchorY, Math.round(quad.bl.x * 32), Math.round(quad.bl.y * 32), quad.texPrimary.x, quad.texPrimary.y + quad.texPrimary.h, newSizeX, newSizeY, pixelOffsetTLX, pixelOffsetBRY, minFontScaleX, minFontScaleY);
                    this.icon.updateSymbolVertexData(currentVertexOffset + 3, anchorX, anchorY, Math.round(quad.br.x * 32), Math.round(quad.br.y * 32), quad.texPrimary.x + quad.texPrimary.w, quad.texPrimary.y + quad.texPrimary.h, newSizeX, newSizeY, pixelOffsetBRX, pixelOffsetBRY, minFontScaleX, minFontScaleY);
                    currentVertexOffset += 4;
                }

                // Clear remaining vertices to prevent rendering stale data from previous appearances
                const verticesUsed = quadsToUpdate * 4;
                const remainingVertices = symbolInstance.numIconVertices - verticesUsed;
                for (let k = 0; k < remainingVertices; ++k) {
                    this.icon.updateSymbolVertexData(currentVertexOffset + k, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
                }

                return {vertexOffsetDelta: symbolInstance.numIconVertices, hasChanges: true};
            }
        } else if (featureData.usesAppearanceIconAsPlaceholder) {
            // Hide the placeholder icon when no appearance is active: zero out texture coordinates
            // so the quad is invisible while the vertex buffer structure is preserved.
            // usesAppearanceIconAsPlaceholder can only be true when appearances existed at populate() time.
            this.icon.updateSymbolVertexData(vertexOffset, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
            this.icon.updateSymbolVertexData(vertexOffset + 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
            this.icon.updateSymbolVertexData(vertexOffset + 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
            this.icon.updateSymbolVertexData(vertexOffset + 3, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
            return {vertexOffsetDelta: symbolInstance.numIconVertices, hasChanges: true};
        } else if (featureData.isUsingAppearanceIconVertexData) {
            // No active appearance but vertex buffer still holds appearance data — restore original layout vertices.
            this.icon.restoreSymbolVertexData(vertexOffset, featureData.layoutBasedIconVertexData);
            featureData.isUsingAppearanceIconVertexData = false;
            return {vertexOffsetDelta: featureData.layoutBasedIconVertexData.length / SYMBOL_VERTEX_STRIDE, hasChanges: true};
        }
        return {vertexOffsetDelta: symbolInstance.numIconVertices, hasChanges: false};
    }

    private updateSymbolInstanceTextVertices(
        symbolInstance: SymbolInstance,
        featureData: AppearanceFeatureData,
        activeAppearanceIndex: number,
        evaluationFeature: EvaluationFeature,
        vertexOffset: number,
        ctx: AppearanceTextUpdateContext,
    ): {vertexOffsetDelta: number; hasChanges: boolean} {
        const {canonical, layer, textScaleFactor, imageMap, featureState, layoutTextOffset, layoutTextSize, layoutTextRotate, layoutMinZoomSize, layoutMaxZoomSize, layoutTextSizeMinZoom, layoutTextSizeMaxZoom, availableImages} = ctx;
        const hasHorizontalText = symbolInstance.numHorizontalGlyphVertices > 0;
        const hasVerticalText = symbolInstance.numVerticalGlyphVertices > 0;
        const hasText = hasHorizontalText || hasVerticalText;

        if (!hasText) {
            return {vertexOffsetDelta: hasText ? symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices : 0, hasChanges: false};
        }

        if (featureData.activeAppearanceIndex === activeAppearanceIndex) {
            return {vertexOffsetDelta: symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices, hasChanges: false};
        }

        const activeAppearance = activeAppearanceIndex >= 0 ? layer.appearances[activeAppearanceIndex] : null;
        if (activeAppearance && featureData.textShaping) {
            // Get appearance-based text properties and fallback to layout ones
            const {appearanceTextOffset: textOffset, appearanceTextRotate: textRotate, appearanceTextSize: textSizeValue} = getAppearanceTextValues(activeAppearance, layer, evaluationFeature as SymbolFeature, canonical, layoutTextOffset, layoutTextRotate, layoutTextSize);

            // Recompute fontScale with appearance data so it will be correctly used during icon update later
            featureData.fontScale = computeFontScale(textSizeValue, featureData.textScaleFactor);
            const unevaluatedTextSize = activeAppearance.getUnevaluatedLayoutProperty('text-size') as PropertyValue<number, PossiblyEvaluatedPropertyValue<number>>;
            let textSizeData = this.textSizeData;
            let minZoom = layoutTextSizeMinZoom;
            let maxZoom = layoutTextSizeMaxZoom;
            if (activeAppearance.hasLayoutProperty('text-size')) {
                textSizeData = getSizeData(this.zoom, unevaluatedTextSize, this.worldview, availableImages);
                minZoom = this.textSizeData.kind === 'composite' ? this.textSizeData.minZoom : 0;
                maxZoom = this.textSizeData.kind === 'composite' ? this.textSizeData.maxZoom : 0;
            }

            // Update the shaped text offset
            // When the text is not on lines, top/bottom/left/right are basically
            // the layout offset. When text is on a line, it will have been translated
            // and we need to compute the offset from the layout offset and apply it
            // to the new textOffset
            const topDiff = featureData.textShaping.top - layoutTextOffset[1];
            const bottomDiff = featureData.textShaping.bottom - layoutTextOffset[1];
            const leftDiff = featureData.textShaping.left - layoutTextOffset[0];
            const rightDiff = featureData.textShaping.right - layoutTextOffset[0];

            // Update the offset but DON'T scale the bounds - keep them in glyph space
            // fitIconToText will apply the fontScale to convert to pixels at the new text size
            featureData.textShaping.top = textOffset[1] + topDiff;
            featureData.textShaping.bottom = textOffset[1] + bottomDiff;
            featureData.textShaping.left = textOffset[0] + leftDiff;
            featureData.textShaping.right = textOffset[0] + rightDiff;

            // Generate new text quads with appearance properties
            const textQuads = getGlyphQuads(
                {x: symbolInstance.tileAnchorX, y: symbolInstance.tileAnchorY} as Anchor,
                featureData.textShaping,
                textOffset,
                layer,
                false,
                evaluationFeature,
                imageMap,
                this.allowVerticalPlacement,
                textRotate
            );

            // Calculate size vertex data with appearance text-size
            const minZoomSize = textSizeValue && textSizeData.kind === 'composite' ? unevaluatedTextSize.possiblyEvaluate(new EvaluationParameters(minZoom, {}), canonical).evaluate(evaluationFeature, featureState, canonical) : layoutMinZoomSize;
            const maxZoomSize = textSizeValue && textSizeData.kind === 'composite' ? unevaluatedTextSize.possiblyEvaluate(new EvaluationParameters(maxZoom, {}), canonical).evaluate(evaluationFeature, featureState, canonical) : layoutMaxZoomSize;
            const textSizeDataKind = packSizeForVertex(activeAppearance.name, textSizeData, textSizeValue, textScaleFactor, minZoomSize, maxZoomSize);
            const textSizeY = Array.isArray(textSizeDataKind) ? textSizeDataKind[1] : textSizeValue;
            const symbolSizeX = 0; // Not used in appearances
            const symbolSizeY = (Math.min(MAX_PACKED_SIZE, Math.round(textSizeY * SIZE_PACK_FACTOR)) << 1) + 1; // Appearance flag

            // Store the layout-based vertex data to restore it later if it's the first time we use an appearance for this feature
            if (!featureData.isUsingAppearanceTextVertexData) {
                featureData.isUsingAppearanceTextVertexData = true;
                featureData.layoutBasedTextVertexData = this.text.snapshotSymbolVertexData(vertexOffset, symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices);
            }

            // Update vertex data for text quads
            for (let j = 0; j < textQuads.length && j < (symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices) / 4; ++j) {
                const quad = textQuads[j];
                const y = quad.glyphOffset[1];

                const anchorX = symbolInstance.tileAnchorX;
                const anchorY = symbolInstance.tileAnchorY;

                // Update 4 vertices for this quad
                const vertexIndex = vertexOffset + j * 4;
                this.text.updateSymbolVertexData(vertexIndex, anchorX, anchorY, Math.round(quad.tl.x * 32), Math.round((y + quad.tl.y) * 32), quad.texPrimary.x, quad.texPrimary.y, symbolSizeX, symbolSizeY, quad.pixelOffsetTL.x, quad.pixelOffsetTL.y, quad.minFontScaleX, quad.minFontScaleY);
                this.text.updateSymbolVertexData(vertexIndex + 1, anchorX, anchorY, Math.round(quad.tr.x * 32), Math.round((y + quad.tr.y) * 32), quad.texPrimary.x + quad.texPrimary.w, quad.texPrimary.y, symbolSizeX, symbolSizeY, quad.pixelOffsetBR.x, quad.pixelOffsetTL.y, quad.minFontScaleX, quad.minFontScaleY);
                this.text.updateSymbolVertexData(vertexIndex + 2, anchorX, anchorY, Math.round(quad.bl.x * 32), Math.round((y + quad.bl.y) * 32), quad.texPrimary.x, quad.texPrimary.y + quad.texPrimary.h, symbolSizeX, symbolSizeY, quad.pixelOffsetTL.x, quad.pixelOffsetBR.y, quad.minFontScaleX, quad.minFontScaleY);
                this.text.updateSymbolVertexData(vertexIndex + 3, anchorX, anchorY, Math.round(quad.br.x * 32), Math.round((y + quad.br.y) * 32), quad.texPrimary.x + quad.texPrimary.w, quad.texPrimary.y + quad.texPrimary.h, symbolSizeX, symbolSizeY, quad.pixelOffsetBR.x, quad.pixelOffsetBR.y, quad.minFontScaleX, quad.minFontScaleY);
            }

            return {vertexOffsetDelta: symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices, hasChanges: true};
        } else if (featureData.isUsingAppearanceTextVertexData) {
            // No active appearance but vertex buffer still holds appearance data — restore original layout vertices.
            this.text.restoreSymbolVertexData(vertexOffset, featureData.layoutBasedTextVertexData);
            featureData.isUsingAppearanceTextVertexData = false;
            return {vertexOffsetDelta: featureData.layoutBasedTextVertexData.length / SYMBOL_VERTEX_STRIDE, hasChanges: true};
        } else {
            return {vertexOffsetDelta: symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices, hasChanges: false};
        }
    }

    update(states: FeatureStates, vtLayer: VectorTileLayer, availableImages: ImageId[], imagePositions: SpritePositions, layers: ReadonlyArray<TypedStyleLayer>, isBrightnessChanged: boolean, brightness?: number | null, canonical?: CanonicalTileID) {
        // Traditional attribute-based updates
        this.text.programConfigurations.updatePaintArrays(states, vtLayer, layers, availableImages, imagePositions, isBrightnessChanged, brightness, this.worldview);
        this.icon.programConfigurations.updatePaintArrays(states, vtLayer, layers, availableImages, imagePositions, isBrightnessChanged, brightness, this.worldview);

        // UBO-based updates
        if (canonical) {
            // Update all features when brightness or other dynamic expressions change
            if (isBrightnessChanged) {
                if (this.text.uboBinder) {
                    this.text.uboBinder.updateDynamicExpressions(
                        layers[0] as SymbolStyleLayer,
                        vtLayer,
                        canonical,
                        availableImages,
                        states,
                        brightness
                    );
                }

                if (this.icon.uboBinder) {
                    this.icon.uboBinder.updateDynamicExpressions(
                        layers[0] as SymbolStyleLayer,
                        vtLayer,
                        canonical,
                        availableImages,
                        states,
                        brightness
                    );
                }
            } else if (Object.keys(states).length > 0) {
                // Update specific features when feature-state changes
                const featureIds = new Set<string | number>(Object.keys(states).map(id => {
                    // Convert to number only if it's a safe integer to avoid precision loss
                    const numId = Number(id);
                    if (!isNaN(numId) && Number.isSafeInteger(numId) && String(numId) === id) {
                        return numId;
                    }
                    return id;
                }));

                if (this.text.uboBinder) {
                    this.text.uboBinder.updateFeatures(
                        featureIds,
                        layers[0] as SymbolStyleLayer,
                        vtLayer,
                        canonical,
                        availableImages,
                        states,
                        brightness
                    );
                }

                if (this.icon.uboBinder) {
                    this.icon.uboBinder.updateFeatures(
                        featureIds,
                        layers[0] as SymbolStyleLayer,
                        vtLayer,
                        canonical,
                        availableImages,
                        states,
                        brightness
                    );
                }
            }
        }
    }

    updateRoadElevation(canonical: CanonicalTileID) {
        if (this.elevationType !== 'road' || !this.elevationFeatures) {
            return;
        }

        if (this.elevationStateComplete) {
            // Road elevation is updated only once
            return;
        }

        this.elevationStateComplete = true;
        this.hasAnyZOffset = false;
        let dataChanged = false;

        const tileToMeters = tileToMeter(canonical);
        const metersToTile = 1.0 / tileToMeters;
        let hasTextOrientation = false;
        let hasIconOrientation = false;

        for (let s = 0; s < this.symbolInstances.length; s++) {
            const symbolInstance = this.symbolInstances.get(s);
            const orientedXAxis = vec3.fromValues(1, 0, 0);
            const orientedYAxis = vec3.fromValues(0, 1, 0);

            const {
                numHorizontalGlyphVertices,
                numVerticalGlyphVertices,
                numIconVertices,
                numVerticalIconVertices
            } = symbolInstance;

            const hasText = numHorizontalGlyphVertices > 0 || numVerticalGlyphVertices > 0;
            const hasIcon = numIconVertices > 0;

            const elevationFeature = this.elevationFeatures[symbolInstance.elevationFeatureIndex];
            if (elevationFeature) {
                // Add 7.5cm offset to reduce z-fighting issues
                const anchor = new Point(symbolInstance.tileAnchorX, symbolInstance.tileAnchorY);
                const newZOffset = 0.075 + elevationFeature.pointElevation(anchor);
                if (symbolInstance.zOffset !== newZOffset) {
                    dataChanged = true;
                    symbolInstance.zOffset = newZOffset;
                }

                if (newZOffset !== 0) {
                    this.hasAnyZOffset = true;
                }

                const slopeNormal = elevationFeature.computeSlopeNormal(anchor, metersToTile);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const rotation = quat.rotationTo(quat.create(), vec3.fromValues(0, 0, 1), slopeNormal);

                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                vec3.transformQuat(orientedXAxis, orientedXAxis, rotation);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                vec3.transformQuat(orientedYAxis, orientedYAxis, rotation);

                orientedXAxis[2] *= tileToMeters;
                orientedYAxis[2] *= tileToMeters;

                // Check for existence of non-default orientation data
                if (orientedXAxis[0] !== 1.0 || orientedXAxis[1] !== 0.0 || orientedXAxis[2] !== 0.0 ||
                    orientedYAxis[0] !== 0.0 || orientedYAxis[1] !== 1.0 || orientedYAxis[2] !== 0.0) {
                    hasTextOrientation = hasTextOrientation || hasText;
                    hasIconOrientation = hasIconOrientation || hasIcon;
                }
            }

            if (hasText) {
                addOrientationVertex(this.text.orientationVertexArray, numHorizontalGlyphVertices, orientedXAxis, orientedYAxis);
                addOrientationVertex(this.text.orientationVertexArray, numVerticalGlyphVertices, orientedXAxis, orientedYAxis);
            }
            if (hasIcon) {
                const {placedIconSymbolIndex, verticalPlacedIconSymbolIndex} = symbolInstance;
                if (placedIconSymbolIndex >= 0) {
                    addOrientationVertex(this.icon.orientationVertexArray, numIconVertices, orientedXAxis, orientedYAxis);
                }

                if (verticalPlacedIconSymbolIndex >= 0) {
                    addOrientationVertex(this.icon.orientationVertexArray, numVerticalIconVertices, orientedXAxis, orientedYAxis);
                }
            }
        }

        // If there is no orientation data, clear the vertex arrays so vertex buffers won't be created.
        if (!hasTextOrientation) {
            this.text.orientationVertexArray = undefined;
        }
        if (!hasIconOrientation) {
            this.icon.orientationVertexArray = undefined;
        }

        if (dataChanged) {
            this.zOffsetBuffersNeedUpload = true;
            this.zOffsetSortDirty = true;
        }
    }

    updateZOffset() {
        // z offset is expected to change less frequently than the placement opacity and, if values are the same,
        // avoid uploading arrays to buffers.
        const addZOffsetTextVertex = (array: ZOffsetVertexArray, numVertices: number, value: number) => {
            currentTextZOffsetVertex += numVertices;
            if (currentTextZOffsetVertex > array.length) {
                array.resize(currentTextZOffsetVertex);
            }
            for (let i = -numVertices; i < 0; i++) {
                array.emplace(i + currentTextZOffsetVertex, value);
            }
        };
        const addZOffsetIconVertex = (array: ZOffsetVertexArray, numVertices: number, value: number) => {
            currentIconZOffsetVertex += numVertices;
            if (currentIconZOffsetVertex > array.length) {
                array.resize(currentIconZOffsetVertex);
            }
            for (let i = -numVertices; i < 0; i++) {
                array.emplace(i + currentIconZOffsetVertex, value);
            }
        };

        const updateZOffset = this.zOffsetBuffersNeedUpload;
        if (!updateZOffset) return;
        this.zOffsetBuffersNeedUpload = false;
        let currentTextZOffsetVertex = 0;
        let currentIconZOffsetVertex = 0;
        for (let s = 0; s < this.symbolInstances.length; s++) {
            const symbolInstance = this.symbolInstances.get(s);
            const {
                numHorizontalGlyphVertices,
                numVerticalGlyphVertices,
                numIconVertices
            } = symbolInstance;
            const zOffset = symbolInstance.zOffset;
            const hasText = numHorizontalGlyphVertices > 0 || numVerticalGlyphVertices > 0;
            const hasIcon = numIconVertices > 0;
            if (hasText) {
                addZOffsetTextVertex(this.text.zOffsetVertexArray, numHorizontalGlyphVertices, zOffset);
                addZOffsetTextVertex(this.text.zOffsetVertexArray, numVerticalGlyphVertices, zOffset);
            }
            if (hasIcon) {
                const {placedIconSymbolIndex, verticalPlacedIconSymbolIndex} = symbolInstance;
                if (placedIconSymbolIndex >= 0) {
                    addZOffsetIconVertex(this.icon.zOffsetVertexArray, numIconVertices, zOffset);
                }

                if (verticalPlacedIconSymbolIndex >= 0) {
                    addZOffsetIconVertex(this.icon.zOffsetVertexArray, symbolInstance.numVerticalIconVertices, zOffset);
                }
            }
        }

        if (this.text.zOffsetVertexBuffer) {
            this.text.zOffsetVertexBuffer.updateData(this.text.zOffsetVertexArray);
            assert(this.text.zOffsetVertexBuffer.length === this.text.layoutVertexArray.length);
        }
        if (this.icon.zOffsetVertexBuffer) {
            this.icon.zOffsetVertexBuffer.updateData(this.icon.zOffsetVertexArray);
            assert(this.icon.zOffsetVertexBuffer.length === this.icon.layoutVertexArray.length);
        }

    }

    isEmpty(): boolean {
        // When the bucket encounters only rtl-text but the plugin isn't loaded, no symbol instances will be created.
        // In order for the bucket to be serialized, and not discarded as an empty bucket both checks are necessary.
        return this.symbolInstances.length === 0 && !this.hasRTLText;
    }

    uploadPending(): boolean {
        return !this.uploaded || this.text.programConfigurations.needsUpload || this.icon.programConfigurations.needsUpload;
    }

    upload(context: Context, canonical?: CanonicalTileID, featureState?: FeatureStates, availableImages?: Array<ImageId>, globalProperties?: GlobalProperties) {
        if (!this.uploaded && this.hasDebugData()) {
            this.textCollisionBox.upload(context);
            this.iconCollisionBox.upload(context);
        }

        this.text.upload(context, this.sortFeaturesByY, !this.uploaded, this.text.programConfigurations.needsUpload, this.zOffsetBuffersNeedUpload, this.hasAppearances);

        if (this.hasAppearances === null) {
            this.hasAppearances = this.layers.some(layer => layer.appearances && layer.appearances.length > 0);
        }
        this.icon.upload(context, this.sortFeaturesByY, !this.uploaded, this.icon.programConfigurations.needsUpload, this.zOffsetBuffersNeedUpload, this.hasAppearances);
        this.uploaded = true;
    }

    updateAppearances(canonical?: CanonicalTileID, featureState?: FeatureStates, availableImages?: Array<ImageId>, globalProperties?: GlobalProperties, imageManager?: ImageManager, featureStateChanged: boolean = false) {
        // Note: featureState can be undefined or {} for features without feature-state
        // Appearances can activate based on zoom/pitch alone without feature-state
        const noChanges = {hasLayoutChanges: false, hasUboChanges: false};
        if (!canonical || !availableImages || !this.featureAppearanceData) {
            return noChanges;
        }
        // Ensure featureState is at least an empty object for property evaluation
        const states = featureState || {};

        const hasIconData = this.icon.layoutVertexArray && this.icon.layoutVertexArray.length > 0 && this.icon.layoutVertexArray.arrayBuffer;
        const hasTextData = this.text.layoutVertexArray && this.text.layoutVertexArray.length > 0 && this.text.layoutVertexArray.arrayBuffer;

        if (!hasIconData && !hasTextData) {
            return noChanges;
        }

        const layer = this.layers[0];
        const layout = layer.layout;

        // Create or recreate FeatureAppearances when the layer's appearances change structurally.
        if (!this.featureAppearances ||
            this.featureAppearances.appearancesVersion !== layer.appearancesVersion) {
            this.featureAppearances = new FeatureAppearances(layer.appearances, layer.appearancesVersion);
        }

        const appearanceUpdateResult: AppearanceUpdateResult = this.featureAppearances.update(
            globalProperties, featureStateChanged
        );
        if (appearanceUpdateResult.kind === 'no-changes') return noChanges;

        // Prepare icon parameters
        let iconScaleFactor = 1;
        let iconVertexOffset = 0;
        let hasIconChanges = false;
        let hasUboChanges = false;
        if (hasIconData) {
            const [iconSizeScaleRangeMin, iconSizeScaleRangeMax] = layout.get('icon-size-scale-range');
            iconScaleFactor = clamp(1, iconSizeScaleRangeMin, iconSizeScaleRangeMax);
        }

        // Prepare text parameters
        let textScaleFactor = 1;
        let textVertexOffset = 0;
        let hasTextChanges = false;
        if (hasTextData) {
            const [textSizeScaleRangeMin, textSizeScaleRangeMax] = layout.get('text-size-scale-range');
            textScaleFactor = clamp(1, textSizeScaleRangeMin, textSizeScaleRangeMax);
        }

        // Construct imageMap from imageManager for inline images in formatted text
        const imageMap = new Map<StringifiedImageVariant, StyleImage>();
        if (imageManager && availableImages) {
            for (const imageId of availableImages) {
                const image = imageManager.getImage(imageId, layer.scope);
                if (image) {
                    // Create a default ImageVariant from the ImageId
                    const imageVariant = new ImageVariantClass(imageId.toString());
                    imageMap.set(imageVariant.toString(), image);
                }
            }
        }

        for (let s = 0; s < this.symbolInstances.length; s++) {
            const symbolInstance = this.symbolInstances.get(s);
            const featureData = this.getAppearanceFeatureData(symbolInstance.featureIndex);
            if (!featureData) continue;

            const featureId = featureData.id;
            const featureStateForThis = featureId !== undefined ? states[String(featureId)] : undefined;

            // Create a evaluation feature. Note that the only important parts are the id and properties
            const evaluationFeature = {
                type: 'Point' as const, // Type doesn't matter
                id: featureData.id,
                properties: featureData.properties,
                geometry: [] // Geometry doesn't matter
            };

            let activeAppearanceIndex: number;
            if (appearanceUpdateResult.kind === 'all-features') {
                activeAppearanceIndex = appearanceUpdateResult.appearanceIndex;
            } else {
                // 'per-feature': evaluate conditions with feature context
                const found = layer.appearances ? layer.appearances.findIndex(a => a.isActive({globals: globalProperties, feature: evaluationFeature, canonical, featureState: featureStateForThis})) : -1;
                activeAppearanceIndex = found >= 0 ? found : -1;
            }
            const appearanceChanged = featureData.activeAppearanceIndex !== activeAppearanceIndex;

            if (hasTextData) {
                const layoutTextSizeExpression = layout.get('text-size');
                const textSizeDataMinZoom = this.textSizeData.kind === 'composite' ? this.textSizeData.minZoom : 0;
                const textSizeDataMaxZoom = this.textSizeData.kind === 'composite' ? this.textSizeData.maxZoom : 0;
                const textCtx: AppearanceTextUpdateContext = {
                    canonical,
                    layer,
                    featureState: featureStateForThis,
                    availableImages,
                    textScaleFactor,
                    imageMap,
                    layoutTextOffset: layout.get('text-offset').evaluate(evaluationFeature, featureStateForThis, canonical).map(v => v * ONE_EM) as [number, number],
                    layoutTextSize: layoutTextSizeExpression.evaluate(evaluationFeature, featureStateForThis, canonical),
                    layoutTextRotate: layout.get('text-rotate').evaluate(evaluationFeature, featureStateForThis, canonical),
                    layoutMinZoomSize: layoutTextSizeExpression.evaluate(evaluationFeature, {zoom: textSizeDataMinZoom}, canonical),
                    layoutMaxZoomSize: layoutTextSizeExpression.evaluate(evaluationFeature, {zoom: textSizeDataMaxZoom}, canonical),
                    layoutTextSizeMinZoom: textSizeDataMinZoom,
                    layoutTextSizeMaxZoom: textSizeDataMaxZoom,
                };
                const textResult = this.updateSymbolInstanceTextVertices(
                    symbolInstance, featureData, activeAppearanceIndex, evaluationFeature, textVertexOffset, textCtx
                );
                textVertexOffset += textResult.vertexOffsetDelta;
                hasTextChanges = hasTextChanges || textResult.hasChanges;
            }

            if (hasIconData) {
                const iconCtx: AppearanceIconUpdateContext = {
                    canonical,
                    layer,
                    featureState: featureStateForThis,
                    availableImages,
                    globalProperties,
                    iconScaleFactor,
                    layoutIconOffset: layout.get('icon-offset').evaluate(evaluationFeature, featureStateForThis, canonical),
                    layoutIconSize: layout.get('icon-size').evaluate(evaluationFeature, featureStateForThis, canonical, availableImages),
                    layoutIconRotate: layout.get('icon-rotate').evaluate(evaluationFeature, featureStateForThis, canonical),
                };
                const iconResult = this.updateSymbolInstanceIconVertices(
                    symbolInstance, featureData, activeAppearanceIndex, evaluationFeature, iconVertexOffset, iconCtx
                );
                iconVertexOffset += iconResult.vertexOffsetDelta;
                hasIconChanges = hasIconChanges || iconResult.hasChanges;
            }

            if (appearanceChanged) {
                const vtFeatureIndex = symbolInstance.featureIndex;
                const brightness = globalProperties ? globalProperties.brightness : null;
                const fState = featureStateForThis || {};
                const activeAppearance = activeAppearanceIndex >= 0 ? layer.appearances[activeAppearanceIndex] : null;
                if (this.text.uboBinder) {
                    // updateDynamicExpressions/updateFeatures, which set the layer, may not have
                    // been called for zoom/pitch-only condition changes so we do it here
                    this.text.uboBinder.layer = layer;
                    hasUboChanges = this.text.uboBinder.updateFeaturePaintForAppearance(vtFeatureIndex, evaluationFeature, fState, canonical, availableImages, brightness, activeAppearance) || hasUboChanges;
                }
                if (this.icon.uboBinder) {
                    // updateDynamicExpressions/updateFeatures, which set the layer, may not have
                    // been called for zoom/pitch-only condition changes so we do it here
                    this.icon.uboBinder.layer = layer;
                    hasUboChanges = this.icon.uboBinder.updateFeaturePaintForAppearance(vtFeatureIndex, evaluationFeature, fState, canonical, availableImages, brightness, activeAppearance) || hasUboChanges;
                }
            }

            featureData.activeAppearanceIndex = activeAppearanceIndex;
        }

        // Update GPU buffers if there were changes
        if (hasIconChanges && this.icon.layoutVertexBuffer && this.icon.layoutVertexArray.arrayBuffer !== null) {
            if (this.icon.layoutVertexArray.length === this.icon.layoutVertexBuffer.length) {
                this.icon.layoutVertexBuffer.updateData(this.icon.layoutVertexArray);
            }
        }

        if (hasTextChanges && this.text.layoutVertexBuffer && this.text.layoutVertexArray.arrayBuffer !== null) {
            if (this.text.layoutVertexArray.length === this.text.layoutVertexBuffer.length) {
                this.text.layoutVertexBuffer.updateData(this.text.layoutVertexArray);
            }
        }

        return {hasLayoutChanges: hasIconChanges || hasTextChanges, hasUboChanges};
    }

    destroyDebugData() {
        this.textCollisionBox.destroy();
        this.iconCollisionBox.destroy();
    }

    getProjection(): Projection {
        if (!this.projectionInstance) {
            this.projectionInstance = getProjection(this.projection);
        }
        return this.projectionInstance;
    }

    destroy() {
        this.text.destroy();
        this.icon.destroy();

        if (this.hasDebugData()) {
            this.destroyDebugData();
        }
    }

    addToLineVertexArray(anchor: Anchor, line: Array<Point>): LineVertexRange {
        const lineStartIndex = this.lineVertexArray.length;
        if (anchor.segment !== undefined) {
            for (const {x, y} of line) {
                this.lineVertexArray.emplaceBack(x, y);
            }
        }
        return {
            lineStartIndex,
            lineLength: this.lineVertexArray.length - lineStartIndex
        };
    }

    addSymbols(arrays: SymbolBuffers,
               quads: Array<SymbolQuad>,
               sizeVertex: number[],
               lineOffset: [number, number],
               alongLine: boolean,
               feature: SymbolFeature,
               writingMode: number | undefined,
               globe: GlobeData | null | undefined,
               tileAnchor: Anchor,
               lineStartIndex: number,
               lineLength: number,
               associatedIconIndex: number,
               availableImages: ImageId[],
               canonical: CanonicalTileID,
               brightness: number | null | undefined,
               hasAnySecondaryIcon: boolean,
               symbolInstanceIndex: number,
               maxQuadCount: number) {
        const indexArray = arrays.indexArray;
        const layoutVertexArray = arrays.layoutVertexArray;
        const globeExtVertexArray = arrays.globeExtVertexArray;

        // Use maxQuadCount for segment preparation to ensure enough space for appearance variants
        const quadCountForSegment = maxQuadCount;

        // Get batch index before calling prepareSegment so it can force segment boundaries
        const batchIndex = arrays.uboBinder ? arrays.uboBinder.getCurrentBatchIndex() : undefined;
        const segment = arrays.segments.prepareSegment(4 * quadCountForSegment, layoutVertexArray, indexArray, this.canOverlap ? feature.sortKey : undefined, batchIndex);

        const glyphOffsetArrayStart = this.glyphOffsetArray.length;
        const vertexStartIndex = segment.vertexLength;

        const angle = (this.allowVerticalPlacement && writingMode === WritingMode.vertical) ? Math.PI / 2 : 0;

        const sections = feature.text && feature.text.sections;

        // Track vertex count for UBO feature ID population
        let lastPopulatedVertexCount = layoutVertexArray.length;
        let lastUboIndex = -1; // Track the last UBO index for null vertex padding

        for (let i = 0; i < quads.length; i++) {
            const {tl, tr, bl, br, texPrimary, texSecondary, pixelOffsetTL, pixelOffsetBR, minFontScaleX, minFontScaleY, glyphOffset, isSDF, sectionIndex} = quads[i];
            const index = segment.vertexLength;

            const y = glyphOffset[1];
            addVertex(layoutVertexArray, tileAnchor.x, tileAnchor.y, tl.x, y + tl.y, texPrimary.x, texPrimary.y, sizeVertex, isSDF, pixelOffsetTL.x, pixelOffsetTL.y, minFontScaleX, minFontScaleY);
            addVertex(layoutVertexArray, tileAnchor.x, tileAnchor.y, tr.x, y + tr.y, texPrimary.x + texPrimary.w, texPrimary.y, sizeVertex, isSDF, pixelOffsetBR.x, pixelOffsetTL.y, minFontScaleX, minFontScaleY);
            addVertex(layoutVertexArray, tileAnchor.x, tileAnchor.y, bl.x, y + bl.y, texPrimary.x, texPrimary.y + texPrimary.h, sizeVertex, isSDF, pixelOffsetTL.x, pixelOffsetBR.y, minFontScaleX, minFontScaleY);
            addVertex(layoutVertexArray, tileAnchor.x, tileAnchor.y, br.x, y + br.y, texPrimary.x + texPrimary.w, texPrimary.y + texPrimary.h, sizeVertex, isSDF, pixelOffsetBR.x, pixelOffsetBR.y, minFontScaleX, minFontScaleY);

            if (globe) {
                const {x, y, z} = globe.anchor;
                const [ux, uy, uz] = globe.up;
                addGlobeVertex(globeExtVertexArray, x, y, z, ux, uy, uz);
                addGlobeVertex(globeExtVertexArray, x, y, z, ux, uy, uz);
                addGlobeVertex(globeExtVertexArray, x, y, z, ux, uy, uz);
                addGlobeVertex(globeExtVertexArray, x, y, z, ux, uy, uz);

                addDynamicAttributes(arrays.dynamicLayoutVertexArray, x, y, z, angle);
            } else {
                addDynamicAttributes(arrays.dynamicLayoutVertexArray, tileAnchor.x, tileAnchor.y, tileAnchor.z, angle);
            }

            // For data-driven cases if at least of one the icon has a transitionable variant
            // we have to load the main variant in cases where the secondary image is not specified
            if (hasAnySecondaryIcon) {
                const tex = texSecondary ? texSecondary : texPrimary;
                addTransitioningVertex(arrays.iconTransitioningVertexArray, tex.x, tex.y);
                addTransitioningVertex(arrays.iconTransitioningVertexArray, tex.x + tex.w, tex.y);
                addTransitioningVertex(arrays.iconTransitioningVertexArray, tex.x, tex.y + tex.h);
                addTransitioningVertex(arrays.iconTransitioningVertexArray, tex.x + tex.w, tex.y + tex.h);
            }

            indexArray.emplaceBack(index, index + 1, index + 2);
            indexArray.emplaceBack(index + 1, index + 2, index + 3);

            segment.vertexLength += 4;
            segment.primitiveLength += 2;

            this.glyphOffsetArray.emplaceBack(glyphOffset[0]);

            if (i === quads.length - 1 || sectionIndex !== quads[i + 1].sectionIndex) {
                arrays.programConfigurations.populatePaintArrays(layoutVertexArray.length, feature, feature.index, {}, availableImages, canonical, brightness, sections && sections[sectionIndex], this.worldview);

                if (arrays.uboBinder && arrays.featureIdArray) {
                    const uboIndex = arrays.uboBinder.populateUBO(
                        feature,
                        feature.index,
                        canonical,
                        availableImages,
                        brightness,
                        sections && sections[sectionIndex],
                        undefined  // context not available on worker thread
                    );
                    lastUboIndex = uboIndex; // Track for null vertex padding
                    // Add feature ID for each vertex in this section
                    const sectionVertexCount = layoutVertexArray.length - lastPopulatedVertexCount;
                    for (let v = 0; v < sectionVertexCount; v++) {
                        arrays.featureIdArray.emplaceBack(uboIndex);
                    }
                    // Update tracking for next section
                    lastPopulatedVertexCount = layoutVertexArray.length;
                }
            }
        }

        // Add zero-padding for unused vertices to ensure consistent buffer structure for appearance variants
        const remainingQuads = maxQuadCount - quads.length;
        if (remainingQuads !== 0) {
            this._addNullVertices(remainingQuads, layoutVertexArray, sizeVertex, globe, globeExtVertexArray, arrays, hasAnySecondaryIcon, segment, indexArray);

            // Add feature IDs for null vertices so they share the last section's UBO entry
            if (arrays.uboBinder && arrays.featureIdArray && lastUboIndex >= 0) {
                const nullVertexCount = remainingQuads * 4; // 4 vertices per quad
                for (let v = 0; v < nullVertexCount; v++) {
                    arrays.featureIdArray.emplaceBack(lastUboIndex);
                }
            }
        }

        const projectedAnchor = globe ? globe.anchor : tileAnchor;

        arrays.placedSymbolArray.emplaceBack(projectedAnchor.x, projectedAnchor.y, projectedAnchor.z, tileAnchor.x, tileAnchor.y,
            glyphOffsetArrayStart, this.glyphOffsetArray.length - glyphOffsetArrayStart, vertexStartIndex,
            lineStartIndex, lineLength, tileAnchor.segment,
            sizeVertex ? sizeVertex[0] : 0, sizeVertex ? sizeVertex[1] : 0,
            lineOffset[0], lineOffset[1],
            writingMode,
            // placedOrientation is null initially; will be updated to horizontal(1)/vertical(2) if placed
            0,
            0,
            // The crossTileID is only filled/used on the foreground for dynamic text anchors
            0,
            associatedIconIndex,
            // flipState is unknown initially; will be updated to flipRequired(1)/flipNotRequired(2) during line label reprojection
            0
        );

        arrays.symbolInstanceIndices.push(symbolInstanceIndex);
    }

    _addNullVertices(num: number, layoutVertexArray: SymbolLayoutArray, sizeVertex: number[], globe: GlobeData,
        globeExtVertexArray: SymbolGlobeExtArray, arrays: SymbolBuffers, hasAnySecondaryIcon: boolean, segment: Segment,
        indexArray: TriangleIndexArray) {
        for (let i = 0; i < num; i++) {
            // Add 4 vertices per remaining quad with zero values
            for (let j = 0; j < 4; j++) {
                addVertex(layoutVertexArray, 0, 0, 0, 0, 0, 0, sizeVertex, false, 0, 0, 0, 0);

                if (globe) {
                    addGlobeVertex(globeExtVertexArray, 0, 0, 0, 0, 0, 0);
                    addDynamicAttributes(arrays.dynamicLayoutVertexArray, 0, 0, 0, 0);
                } else {
                    addDynamicAttributes(arrays.dynamicLayoutVertexArray, 0, 0, 0, 0);
                }

                if (hasAnySecondaryIcon) {
                    addTransitioningVertex(arrays.iconTransitioningVertexArray, 0, 0);
                }
            }

            const quadStartIndex = segment.vertexLength;
            indexArray.emplaceBack(quadStartIndex, quadStartIndex + 1, quadStartIndex + 2);
            indexArray.emplaceBack(quadStartIndex + 1, quadStartIndex + 2, quadStartIndex + 3);

            segment.vertexLength += 4;
            segment.primitiveLength += 2;

            // Add zero glyph offset
            this.glyphOffsetArray.emplaceBack(0);
        }
    }

    _commitLayoutVertex(array: StructArray, boxTileAnchorX: number, boxTileAnchorY: number, boxTileAnchorZ: number, tileAnchorX: number, tileAnchorY: number, extrude: Point) {
        array.emplaceBack(
            // pos
            boxTileAnchorX,
            boxTileAnchorY,
            boxTileAnchorZ,
            // a_anchor_pos
            tileAnchorX,
            tileAnchorY,
            // extrude
            Math.round(extrude.x),
            Math.round(extrude.y));
    }

    _addCollisionDebugVertices(box: CollisionBox, scale: number, arrays: CollisionBuffers, boxTileAnchorX: number, boxTileAnchorY: number, boxTileAnchorZ: number, symbolInstance: SymbolInstance) {
        const segment = arrays.segments.prepareSegment(4, arrays.layoutVertexArray, arrays.indexArray);
        const index = segment.vertexLength;
        const symbolTileAnchorX = symbolInstance.tileAnchorX;
        const symbolTileAnchorY = symbolInstance.tileAnchorY;

        for (let i = 0; i < 4; i++) {
            arrays.collisionVertexArray.emplaceBack(0, 0, 0, 0, 0, 0);
        }

        this._commitDebugCollisionVertexUpdate(arrays.collisionVertexArrayExt, scale, box.padding, symbolInstance.zOffset);

        this._commitLayoutVertex(arrays.layoutVertexArray, boxTileAnchorX, boxTileAnchorY, boxTileAnchorZ, symbolTileAnchorX, symbolTileAnchorY, new Point(box.x1, box.y1));
        this._commitLayoutVertex(arrays.layoutVertexArray, boxTileAnchorX, boxTileAnchorY, boxTileAnchorZ, symbolTileAnchorX, symbolTileAnchorY, new Point(box.x2, box.y1));
        this._commitLayoutVertex(arrays.layoutVertexArray, boxTileAnchorX, boxTileAnchorY, boxTileAnchorZ, symbolTileAnchorX, symbolTileAnchorY, new Point(box.x2, box.y2));
        this._commitLayoutVertex(arrays.layoutVertexArray, boxTileAnchorX, boxTileAnchorY, boxTileAnchorZ, symbolTileAnchorX, symbolTileAnchorY, new Point(box.x1, box.y2));

        segment.vertexLength += 4;

        const indexArray = arrays.indexArray as LineIndexArray;
        indexArray.emplaceBack(index, index + 1);
        indexArray.emplaceBack(index + 1, index + 2);
        indexArray.emplaceBack(index + 2, index + 3);
        indexArray.emplaceBack(index + 3, index);

        segment.primitiveLength += 4;
    }

    _addTextDebugCollisionBoxes(size: InterpolatedSize, zoom: number, collisionBoxArray: CollisionBoxArray, startIndex: number, endIndex: number, instance: SymbolInstance) {
        for (let b = startIndex; b < endIndex; b++) {
            const box: CollisionBox = collisionBoxArray.get(b);
            const scale = this.getSymbolInstanceTextSize(size, instance, zoom, b);

            this._addCollisionDebugVertices(box, scale, this.textCollisionBox, box.projectedAnchorX, box.projectedAnchorY, box.projectedAnchorZ, instance);
        }
    }

    _addIconDebugCollisionBoxes(size: InterpolatedSize, zoom: number, collisionBoxArray: CollisionBoxArray, startIndex: number, endIndex: number, instance: SymbolInstance) {
        for (let b = startIndex; b < endIndex; b++) {
            const box: CollisionBox = collisionBoxArray.get(b);
            const scale = this.getSymbolInstanceIconSize(size, zoom, instance.placedIconSymbolIndex);

            this._addCollisionDebugVertices(box, scale, this.iconCollisionBox, box.projectedAnchorX, box.projectedAnchorY, box.projectedAnchorZ, instance);
        }
    }

    generateCollisionDebugBuffers(zoom: number, collisionBoxArray: CollisionBoxArray, textScaleFactor: number) {
        if (this.hasDebugData()) {
            this.destroyDebugData();
        }

        this.textCollisionBox = new CollisionBuffers(CollisionBoxLayoutArray, collisionBoxLayout.members, LineIndexArray);
        this.iconCollisionBox = new CollisionBuffers(CollisionBoxLayoutArray, collisionBoxLayout.members, LineIndexArray);

        const iconSize = evaluateSizeForZoom(this.iconSizeData, zoom);
        const textSize = evaluateSizeForZoom(this.textSizeData, zoom, textScaleFactor);

        for (let i = 0; i < this.symbolInstances.length; i++) {
            const symbolInstance = this.symbolInstances.get(i);
            this._addTextDebugCollisionBoxes(textSize, zoom, collisionBoxArray, symbolInstance.textBoxStartIndex, symbolInstance.textBoxEndIndex, symbolInstance);
            this._addTextDebugCollisionBoxes(textSize, zoom, collisionBoxArray, symbolInstance.verticalTextBoxStartIndex, symbolInstance.verticalTextBoxEndIndex, symbolInstance);
            this._addIconDebugCollisionBoxes(iconSize, zoom, collisionBoxArray, symbolInstance.iconBoxStartIndex, symbolInstance.iconBoxEndIndex, symbolInstance);
            this._addIconDebugCollisionBoxes(iconSize, zoom, collisionBoxArray, symbolInstance.verticalIconBoxStartIndex, symbolInstance.verticalIconBoxEndIndex, symbolInstance);
        }
    }

    getSymbolInstanceTextSize(textSize: InterpolatedSize, instance: SymbolInstance, zoom: number, boxIndex: number): number {
        const symbolIndex = instance.rightJustifiedTextSymbolIndex >= 0 ?
            instance.rightJustifiedTextSymbolIndex : instance.centerJustifiedTextSymbolIndex >= 0 ?
                instance.centerJustifiedTextSymbolIndex : instance.leftJustifiedTextSymbolIndex >= 0 ?
                    instance.leftJustifiedTextSymbolIndex : instance.verticalPlacedTextSymbolIndex >= 0 ?
                        instance.verticalPlacedTextSymbolIndex : boxIndex;

        const symbol = this.text.placedSymbolArray.get(symbolIndex);
        const featureSize = evaluateSizeForFeature(this.textSizeData, textSize, symbol) / ONE_EM;

        return this.tilePixelRatio * featureSize;
    }

    getSymbolInstanceIconSize(iconSize: InterpolatedSize, zoom: number, iconIndex: number): number {
        const symbol = this.icon.placedSymbolArray.get(iconIndex);
        const featureSize = evaluateSizeForFeature(this.iconSizeData, iconSize, symbol);

        return this.tilePixelRatio * featureSize;
    }

    _commitDebugCollisionVertexUpdate(array: StructArray, scale: number, padding: number, zOffset: number) {
        array.emplaceBack(scale, -padding, -padding, zOffset);
        array.emplaceBack(scale,  padding, -padding, zOffset);
        array.emplaceBack(scale,  padding,  padding, zOffset);
        array.emplaceBack(scale, -padding,  padding, zOffset);
    }

    _updateTextDebugCollisionBoxes(size: InterpolatedSize, zoom: number, collisionBoxArray: CollisionBoxArray, startIndex: number, endIndex: number, instance: SymbolInstance, scaleFactor: number) {
        for (let b = startIndex; b < endIndex; b++) {
            const box: CollisionBox = collisionBoxArray.get(b);
            const scale = this.getSymbolInstanceTextSize(size, instance, zoom, b);
            const array = this.textCollisionBox.collisionVertexArrayExt;
            this._commitDebugCollisionVertexUpdate(array, scale, box.padding, instance.zOffset);
        }
    }

    _updateIconDebugCollisionBoxes(size: InterpolatedSize, zoom: number, collisionBoxArray: CollisionBoxArray, startIndex: number, endIndex: number, instance: SymbolInstance, iconScaleFactor: number) {
        for (let b = startIndex; b < endIndex; b++) {
            const box = (collisionBoxArray.get(b));
            const scale = this.getSymbolInstanceIconSize(size, zoom, instance.placedIconSymbolIndex);
            const array = this.iconCollisionBox.collisionVertexArrayExt;
            this._commitDebugCollisionVertexUpdate(array, scale, box.padding, instance.zOffset);
        }
    }

    updateCollisionDebugBuffers(zoom: number, collisionBoxArray: CollisionBoxArray, textScaleFactor: number, iconScaleFactor: number) {
        if (!this.hasDebugData()) {
            return;
        }

        if (this.hasTextCollisionBoxData()) this.textCollisionBox.collisionVertexArrayExt.clear();
        if (this.hasIconCollisionBoxData()) this.iconCollisionBox.collisionVertexArrayExt.clear();

        const iconSize = evaluateSizeForZoom(this.iconSizeData, zoom, iconScaleFactor);
        const textSize = evaluateSizeForZoom(this.textSizeData, zoom, textScaleFactor);

        for (let i = 0; i < this.symbolInstances.length; i++) {
            const symbolInstance = this.symbolInstances.get(i);
            this._updateTextDebugCollisionBoxes(textSize, zoom, collisionBoxArray, symbolInstance.textBoxStartIndex, symbolInstance.textBoxEndIndex, symbolInstance, textScaleFactor);
            this._updateTextDebugCollisionBoxes(textSize, zoom, collisionBoxArray, symbolInstance.verticalTextBoxStartIndex, symbolInstance.verticalTextBoxEndIndex, symbolInstance, textScaleFactor);
            this._updateIconDebugCollisionBoxes(iconSize, zoom, collisionBoxArray, symbolInstance.iconBoxStartIndex, symbolInstance.iconBoxEndIndex, symbolInstance, iconScaleFactor);
            this._updateIconDebugCollisionBoxes(iconSize, zoom, collisionBoxArray, symbolInstance.verticalIconBoxStartIndex, symbolInstance.verticalIconBoxEndIndex, symbolInstance, iconScaleFactor);
        }

        if (this.hasTextCollisionBoxData() && this.textCollisionBox.collisionVertexBufferExt) {
            this.textCollisionBox.collisionVertexBufferExt.updateData(this.textCollisionBox.collisionVertexArrayExt);
        }
        if (this.hasIconCollisionBoxData() && this.iconCollisionBox.collisionVertexBufferExt) {
            this.iconCollisionBox.collisionVertexBufferExt.updateData(this.iconCollisionBox.collisionVertexArrayExt);
        }
    }

    // These flat arrays are meant to be quicker to iterate over than the source
    // CollisionBoxArray
    _deserializeCollisionBoxesForSymbol(
        collisionBoxArray: CollisionBoxArray,
        textStartIndex: number,
        textEndIndex: number,
        verticalTextStartIndex: number,
        verticalTextEndIndex: number,
        iconStartIndex: number,
        iconEndIndex: number,
        verticalIconStartIndex: number,
        verticalIconEndIndex: number,
    ): CollisionArrays {

        // Only one box allowed per instance
        const collisionArrays: CollisionArrays = {};
        if (textStartIndex < textEndIndex) {
            const {x1, y1, x2, y2, padding, projectedAnchorX, projectedAnchorY, projectedAnchorZ, tileAnchorX, tileAnchorY, featureIndex} = collisionBoxArray.get(textStartIndex);
            collisionArrays.textBox = {x1, y1, x2, y2, padding, projectedAnchorX, projectedAnchorY, projectedAnchorZ, tileAnchorX, tileAnchorY};
            collisionArrays.textFeatureIndex = featureIndex;
        }
        if (verticalTextStartIndex < verticalTextEndIndex) {
            const {x1, y1, x2, y2, padding, projectedAnchorX, projectedAnchorY, projectedAnchorZ, tileAnchorX, tileAnchorY, featureIndex} = collisionBoxArray.get(verticalTextStartIndex);
            collisionArrays.verticalTextBox = {x1, y1, x2, y2, padding, projectedAnchorX, projectedAnchorY, projectedAnchorZ, tileAnchorX, tileAnchorY};
            collisionArrays.verticalTextFeatureIndex = featureIndex;
        }
        if (iconStartIndex < iconEndIndex) {
            const {x1, y1, x2, y2, padding, projectedAnchorX, projectedAnchorY, projectedAnchorZ, tileAnchorX, tileAnchorY, featureIndex} = collisionBoxArray.get(iconStartIndex);
            collisionArrays.iconBox = {x1, y1, x2, y2, padding, projectedAnchorX, projectedAnchorY, projectedAnchorZ, tileAnchorX, tileAnchorY};
            collisionArrays.iconFeatureIndex = featureIndex;
        }
        if (verticalIconStartIndex < verticalIconEndIndex) {
            const {x1, y1, x2, y2, padding, projectedAnchorX, projectedAnchorY, projectedAnchorZ, tileAnchorX, tileAnchorY, featureIndex} = collisionBoxArray.get(verticalIconStartIndex);
            collisionArrays.verticalIconBox = {x1, y1, x2, y2, padding, projectedAnchorX, projectedAnchorY, projectedAnchorZ, tileAnchorX, tileAnchorY};
            collisionArrays.verticalIconFeatureIndex = featureIndex;
        }
        return collisionArrays;
    }

    deserializeCollisionBoxes(collisionBoxArray: CollisionBoxArray) {
        this.collisionArrays = [];
        for (let i = 0; i < this.symbolInstances.length; i++) {
            const symbolInstance = this.symbolInstances.get(i);
            this.collisionArrays.push(this._deserializeCollisionBoxesForSymbol(
                collisionBoxArray,
                symbolInstance.textBoxStartIndex,
                symbolInstance.textBoxEndIndex,
                symbolInstance.verticalTextBoxStartIndex,
                symbolInstance.verticalTextBoxEndIndex,
                symbolInstance.iconBoxStartIndex,
                symbolInstance.iconBoxEndIndex,
                symbolInstance.verticalIconBoxStartIndex,
                symbolInstance.verticalIconBoxEndIndex
            ));
        }
    }

    hasTextData(): boolean {
        return this.text.segments.get().length > 0;
    }

    hasIconData(): boolean {
        return this.icon.segments.get().length > 0;
    }

    hasDebugData(): CollisionBuffers {
        return this.textCollisionBox && this.iconCollisionBox;
    }

    hasTextCollisionBoxData(): boolean {
        return this.hasDebugData() && this.textCollisionBox.segments.get().length > 0;
    }

    hasIconCollisionBoxData(): boolean {
        return this.hasDebugData() && this.iconCollisionBox.segments.get().length > 0;
    }

    hasIconTextFit(): boolean {
        return this.hasAnyIconTextFit;
    }

    addIndicesForPlacedSymbol(iconOrText: SymbolBuffers, placedSymbolIndex: number) {
        const placedSymbol = iconOrText.placedSymbolArray.get(placedSymbolIndex);

        const endIndex = placedSymbol.vertexStartIndex + placedSymbol.numGlyphs * 4;
        for (let vertexIndex = placedSymbol.vertexStartIndex; vertexIndex < endIndex; vertexIndex += 4) {
            iconOrText.indexArray.emplaceBack(vertexIndex, vertexIndex + 1, vertexIndex + 2);
            iconOrText.indexArray.emplaceBack(vertexIndex + 1, vertexIndex + 2, vertexIndex + 3);
        }
    }

    getSortedSymbolIndexes(angle: number): Array<number> {
        if (this.sortedAngle === angle && this.symbolInstanceIndexes !== undefined) {
            return this.symbolInstanceIndexes;
        }
        const sin = Math.sin(angle);
        const cos = Math.cos(angle);
        const rotatedYs = [];
        const featureIndexes = [];
        const result: number[] = [];

        for (let i = 0; i < this.symbolInstances.length; ++i) {
            result.push(i);
            const symbolInstance = this.symbolInstances.get(i);
            rotatedYs.push(Math.round(sin * symbolInstance.tileAnchorX + cos * symbolInstance.tileAnchorY) | 0);
            featureIndexes.push(symbolInstance.featureIndex);
        }

        result.sort((aIndex, bIndex) => (rotatedYs[aIndex] - rotatedYs[bIndex]) || (featureIndexes[bIndex] - featureIndexes[aIndex]));

        return result;
    }

    getSortedIndexesByZOffset(): Array<number> {
        if (!this.zOffsetSortDirty) {
            assert(this.symbolInstanceIndexesSortedZOffset.length === this.symbolInstances.length);
            return this.symbolInstanceIndexesSortedZOffset;
        }
        if (!this.symbolInstanceIndexesSortedZOffset) {
            this.symbolInstanceIndexesSortedZOffset = [];
            for (let i = 0; i < this.symbolInstances.length; ++i) {
                this.symbolInstanceIndexesSortedZOffset.push(i);
            }
        }
        this.zOffsetSortDirty = false;
        return this.symbolInstanceIndexesSortedZOffset.sort((aIndex, bIndex) => this.symbolInstances.get(bIndex).zOffset - this.symbolInstances.get(aIndex).zOffset);
    }

    addToSortKeyRanges(symbolInstanceIndex: number, sortKey: number) {
        const last = this.sortKeyRanges[this.sortKeyRanges.length - 1];
        if (last && last.sortKey === sortKey) {
            last.symbolInstanceEnd = symbolInstanceIndex + 1;
        } else {
            this.sortKeyRanges.push({
                sortKey,
                symbolInstanceStart: symbolInstanceIndex,
                symbolInstanceEnd: symbolInstanceIndex + 1
            });
        }
    }

    sortFeatures(angle: number) {
        if (!this.sortFeaturesByY) return;
        if (this.sortedAngle === angle) return;

        // If the symbols are allowed to overlap sort them by their vertical screen position.
        // The index array buffer is rewritten to reference the (unchanged) vertices in the
        // sorted order.

        // With multiple segments (from UBO batching), sort within each segment independently
        const hasMultipleSegments = this.text.segments.get().length > 1 || this.icon.segments.get().length > 1;
        if (hasMultipleSegments) {
            this.sortFeaturesWithinSegments();
            return;
        }

        // To avoid sorting the actual symbolInstance array we sort an array of indexes.
        this.symbolInstanceIndexes = this.getSortedSymbolIndexes(angle);
        this.sortedAngle = angle;

        this.text.indexArray.clear();
        this.icon.indexArray.clear();

        this.featureSortOrder = [];

        for (const i of this.symbolInstanceIndexes) {
            const symbol = this.symbolInstances.get(i);
            this.featureSortOrder.push(symbol.featureIndex);
            const {
                rightJustifiedTextSymbolIndex: right, centerJustifiedTextSymbolIndex: center,
                leftJustifiedTextSymbolIndex: left, verticalPlacedTextSymbolIndex: vertical,
                placedIconSymbolIndex: icon, verticalPlacedIconSymbolIndex: iconVertical
            } = symbol;

            // Only add a given index the first time it shows up, to avoid duplicate
            // opacity entries when multiple justifications share the same glyphs.
            if (right >= 0) this.addIndicesForPlacedSymbol(this.text, right);
            if (center >= 0 && center !== right) this.addIndicesForPlacedSymbol(this.text, center);
            if (left >= 0 && left !== center && left !== right) this.addIndicesForPlacedSymbol(this.text, left);

            if (vertical >= 0) this.addIndicesForPlacedSymbol(this.text, vertical);
            if (icon >= 0) this.addIndicesForPlacedSymbol(this.icon, icon);
            if (iconVertical >= 0) this.addIndicesForPlacedSymbol(this.icon, iconVertical);
        }

        if (this.text.indexBuffer) this.text.indexBuffer.updateData(this.text.indexArray);
        if (this.icon.indexBuffer) this.icon.indexBuffer.updateData(this.icon.indexArray);
    }

    /**
     * Get or create the symbols-by-batch grouping with caching.
     * Symbols are grouped by UBO batch and sorted by viewport Y position.
     */
    private getOrCreateSortedSymbolsByBatch(sin: number, cos: number): Map<number, Array<{index: number; rotatedY: number; featureIndex: number}>> {
        const activeBinder = (this.text.uboBinder && this.text.uboBinder.maxFeaturesPerBatch > 0) ?
            this.text.uboBinder :
            (this.icon.uboBinder && this.icon.uboBinder.maxFeaturesPerBatch > 0) ?
                this.icon.uboBinder : null;
        assert(activeBinder, 'sortFeaturesWithinSegments requires a valid UBO binder with maxFeaturesPerBatch > 0');
        const batchSize = activeBinder.maxFeaturesPerBatch;

        if (!this.symbolsByBatch) {
            // First time - create and cache the batch grouping
            this.symbolsByBatch = new Map();

            for (let i = 0; i < this.symbolInstances.length; i++) {
                const symbol = this.symbolInstances.get(i);
                const batchIndex = Math.floor(i / batchSize);
                const rotatedY = Math.round(sin * symbol.tileAnchorX + cos * symbol.tileAnchorY) | 0;

                if (!this.symbolsByBatch.has(batchIndex)) {
                    this.symbolsByBatch.set(batchIndex, []);
                }
                this.symbolsByBatch.get(batchIndex).push({
                    index: i,
                    rotatedY,
                    featureIndex: symbol.featureIndex
                });
            }
        } else {
            // Already grouped - just update rotatedY values for the new angle
            for (const batch of this.symbolsByBatch.values()) {
                for (const item of batch) {
                    const symbol = this.symbolInstances.get(item.index);
                    item.rotatedY = Math.round(sin * symbol.tileAnchorX + cos * symbol.tileAnchorY) | 0;
                }
            }
        }

        // Sort each batch by viewport Y position
        for (const batch of this.symbolsByBatch.values()) {
            batch.sort((a, b) => {
                return (a.rotatedY - b.rotatedY) || (b.featureIndex - a.featureIndex);
            });
        }

        return this.symbolsByBatch;
    }

    /**
     * Rebuild text segment indices in sorted order within each segment.
     */
    private rebuildTextSegmentIndices(
        textSegments: Array<Segment>,
        symbolsByBatch: Map<number, Array<{index: number; rotatedY: number; featureIndex: number}>>
    ) {
        for (const textSegment of textSegments) {
            const batchIndex = textSegment.batchIndex;
            if (batchIndex === undefined) continue;

            const batchSymbols = symbolsByBatch.get(batchIndex);
            if (!batchSymbols) {
                textSegment.primitiveOffset = this.text.indexArray.length;
                textSegment.primitiveLength = 0;
                continue;
            }

            textSegment.primitiveOffset = this.text.indexArray.length;
            const startLength = this.text.indexArray.length;

            for (const {index: symbolIndex} of batchSymbols) {
                const symbol = this.symbolInstances.get(symbolIndex);
                const {
                    rightJustifiedTextSymbolIndex: right,
                    centerJustifiedTextSymbolIndex: center,
                    leftJustifiedTextSymbolIndex: left,
                    verticalPlacedTextSymbolIndex: vertical
                } = symbol;

                if (right >= 0) this.addIndicesForPlacedSymbol(this.text, right);
                if (center >= 0 && center !== right) this.addIndicesForPlacedSymbol(this.text, center);
                if (left >= 0 && left !== center && left !== right) this.addIndicesForPlacedSymbol(this.text, left);
                if (vertical >= 0) this.addIndicesForPlacedSymbol(this.text, vertical);
            }

            textSegment.primitiveLength = this.text.indexArray.length - startLength;
        }
    }

    /**
     * Rebuild icon segment indices in sorted order within each segment.
     */
    private rebuildIconSegmentIndices(
        iconSegments: Array<Segment>,
        symbolsByBatch: Map<number, Array<{index: number; rotatedY: number; featureIndex: number}>>
    ) {
        for (const iconSegment of iconSegments) {
            const batchIndex = iconSegment.batchIndex;
            if (batchIndex === undefined) continue;

            const batchSymbols = symbolsByBatch.get(batchIndex);
            if (!batchSymbols) {
                iconSegment.primitiveOffset = this.icon.indexArray.length;
                iconSegment.primitiveLength = 0;
                continue;
            }

            iconSegment.primitiveOffset = this.icon.indexArray.length;
            const startLength = this.icon.indexArray.length;

            for (const {index: symbolIndex} of batchSymbols) {
                const symbol = this.symbolInstances.get(symbolIndex);
                const {
                    placedIconSymbolIndex: icon,
                    verticalPlacedIconSymbolIndex: iconVertical
                } = symbol;

                if (icon >= 0) this.addIndicesForPlacedSymbol(this.icon, icon);
                if (iconVertical >= 0) this.addIndicesForPlacedSymbol(this.icon, iconVertical);
            }

            iconSegment.primitiveLength = this.icon.indexArray.length - startLength;
        }
    }

    /**
     * Sort features within each segment independently.
     * Each segment corresponds to a UBO batch - we rebuild index arrays per-segment.
     */
    sortFeaturesWithinSegments() {
        this.featureSortOrder = [];

        const sin = Math.sin(this.sortedAngle);
        const cos = Math.cos(this.sortedAngle);

        // Get sorted symbols grouped by batch (cached for performance)
        const symbolsByBatch = this.getOrCreateSortedSymbolsByBatch(sin, cos);

        // Build featureSortOrder in batch order
        const sortedBatches = Array.from(symbolsByBatch.keys()).sort((a, b) => a - b);
        for (const batchIndex of sortedBatches) {
            const batchSymbols = symbolsByBatch.get(batchIndex);
            for (const {index: symbolIndex} of batchSymbols) {
                const symbol = this.symbolInstances.get(symbolIndex);
                this.featureSortOrder.push(symbol.featureIndex);
            }
        }

        // Clear and rebuild index arrays with sorted symbols per segment
        this.text.indexArray.clear();
        this.icon.indexArray.clear();

        this.rebuildTextSegmentIndices(this.text.segments.get(), symbolsByBatch);
        this.rebuildIconSegmentIndices(this.icon.segments.get(), symbolsByBatch);

        if (this.text.indexBuffer) this.text.indexBuffer.updateData(this.text.indexArray);
        if (this.icon.indexBuffer) this.icon.indexBuffer.updateData(this.icon.indexArray);
    }

    getElevationFeatureForText(placedSymbolIdx: number): ElevationFeature | undefined {
        const placedSymbols = this.text.placedSymbolArray;
        assert(this.text.symbolInstanceIndices.length === placedSymbols.length);
        const symbolInstanceIndex = this.text.symbolInstanceIndices[placedSymbolIdx];
        const symbolInstance = this.symbolInstances.get(symbolInstanceIndex);
        assert(symbolInstance);
        const elevationFeatureIndex = symbolInstance.elevationFeatureIndex;
        assert(elevationFeatureIndex === 0xffff || elevationFeatureIndex < this.elevationFeatures.length);

        let elevationFeature: ElevationFeature | undefined;
        if (this.elevationFeatures && elevationFeatureIndex < this.elevationFeatures.length) {
            elevationFeature = this.elevationFeatures[elevationFeatureIndex];
        }
        return elevationFeature;
    }
}

register(SymbolBucket, 'SymbolBucket', {
    omit: ['layers', 'collisionBoxArray', 'compareText', 'features']
});

SymbolBucket.addDynamicAttributes = addDynamicAttributes;

export default SymbolBucket;
export {addDynamicAttributes, updateGlobeVertexNormal};
